import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from 'react';
import { MarketSignal, canAccessTier, MembershipTier } from './types';
import { usePortfolioData, getAssetMarket } from './hooks/usePortfolioData';
import { PortfolioSummary } from './components/DashboardWidgets';
import { LandingView } from './components/LandingView';
import { registerPush } from './services/push';
import { auth } from './firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { Settings, HelpCircle, LogOut, Menu, X, Shield, Users, Database, Activity, Target, Clock, TrendingUp, RefreshCw, Sparkles, AlertTriangle, CheckCircle2, AlertCircle, Newspaper, LayoutDashboard, Sun, Moon, Zap, Eye, Lock, Bot, Layers, ChevronLeft, ChevronRight } from 'lucide-react';
import { RegimeBadge } from './components/CommonUI';
import { NotificationCenter } from './components/NotificationCenter';
import { useSettings } from './hooks/useSettings';
import { StorageManager } from './utils/storageManager';
import { useTierGate } from './hooks/useTierGate';
import { TierGate, TierBadge } from './components/TierGate';
import { ReloadPrompt } from './components/ReloadPrompt';
import { SecurityProtection } from './components/SecurityProtection';
import { useTerminology } from './hooks/useTerminology';
import { useLiveTradingMode } from './hooks/useLiveTradingMode';
import { registerCurrentDevice, fetchSystemSecurityConfig } from './services/api';
import { requestPortOnePayment, requestPortOneSubscription } from './services/portonePayment';
import {
  IndicesMarquee,
  FuturesMarquee,
  CommoditiesMarquee,
  StatsGrid,
  MacroTopStats,
  MacroRegimesCard,
  AccountSelector,
  ProposedTradesCard,
  NewRecommendationsGrid,
  AiAnalysisCard,
  TrendRecommendationsWidget
} from './components/DashboardWidgets';
import { PortfolioOverviewWidget } from './components/PortfolioOverviewWidget';
import {
  AssetAllocationCard,
  NewBuyRecommendationsCard
} from './components/PortfolioCards';
import { Wallet as WalletIcon, BarChart3, Calculator as CalcIcon, Calendar as CalendarIcon, Clock as ClockIcon } from 'lucide-react';
import { lazyWithRetry } from './utils/lazyWithRetry';

// ==========================================
// 🚀 Lazy-loaded Components & Modals for Initial Load Optimization
// ==========================================
const AuthView = lazyWithRetry(() => import('./components/AuthView').then(m => ({ default: m.AuthView })));
const MobileApp = lazyWithRetry(() => import('./components/mobile/MobileApp').then(m => ({ default: m.MobileApp })));
const PortfolioManager = lazyWithRetry(() => import('./components/PortfolioManager').then(m => ({ default: m.PortfolioManager })));
const PortfolioTimeSeriesChart = lazyWithRetry(() => import('./components/PortfolioTimeSeriesChart').then(m => ({ default: m.PortfolioTimeSeriesChart })));
const SignalsTable = lazyWithRetry(() => import('./components/SignalsTable').then(m => ({ default: m.SignalsTable })));
const AddAssetModal = lazyWithRetry(() => import('./components/AddAssetModal').then(m => ({ default: m.AddAssetModal })));
const TechnicalAnalysisModal = lazyWithRetry(() => import('./components/TechnicalAnalysisModal').then(m => ({ default: m.TechnicalAnalysisModal })));
const AllocationModal = lazyWithRetry(() => import('./components/AllocationModal').then(m => ({ default: m.AllocationModal })));
const StockAnalysisSlide = lazyWithRetry(() => import('./components/StockAnalysisSlide').then(m => ({ default: m.StockAnalysisSlide })));
const PendingApprovalView = lazyWithRetry(() => import('./components/PendingApprovalView').then(m => ({ default: m.PendingApprovalView })));
const HistoryView = lazyWithRetry(() => import('./components/HistoryView').then(m => ({ default: m.HistoryView })));
const MyPageView = lazyWithRetry(() => import('./components/MyPageView').then(m => ({ default: m.MyPageView })));
const RebalancingCalculator = lazyWithRetry(() => import('./components/RebalancingCalculator').then(m => ({ default: m.RebalancingCalculator })));
const UserGuideView = lazyWithRetry(() => import('./components/UserGuideView').then(m => ({ default: m.UserGuideView })));
const SectorHeatmap = lazyWithRetry(() => import('./components/SectorHeatmap').then(m => ({ default: m.SectorHeatmap })));
const PortfolioCalendar = lazyWithRetry(() => import('./components/PortfolioCalendar').then(m => ({ default: m.PortfolioCalendar })));
const StrategicAllocation = lazyWithRetry(() => import('./components/StrategicAllocation').then(m => ({ default: m.StrategicAllocation })));
const StrategicSimulatorHub = lazyWithRetry(() => import('./components/StrategicSimulatorHub').then(m => ({ default: m.StrategicSimulatorHub })));
const NewsImpactView = lazyWithRetry(() => import('./components/NewsImpactView').then(m => ({ default: m.NewsImpactView })));
const AgentCenter = lazyWithRetry(() => import('./components/agent/AgentCenter').then(m => ({ default: m.AgentCenter })));
const AutoTradingReportView = lazyWithRetry(() => import('./components/AutoTradingReportView').then(m => ({ default: m.AutoTradingReportView })));
const ChatPlusModal = lazyWithRetry(() => import('./components/ChatPlusModal').then(m => ({ default: m.ChatPlusModal })));
const DeviceLimitModal = lazyWithRetry(() => import('./components/DeviceLimitModal').then(m => ({ default: m.DeviceLimitModal })));
const AgentSubscriptionModal = lazyWithRetry(() => import('./components/AgentSubscriptionModal').then(m => ({ default: m.AgentSubscriptionModal })));
const SettingsView = lazyWithRetry(() => import('./components/SettingsView').then(m => ({ default: m.SettingsView })));
const OptionsDataCenter = lazyWithRetry(() => import('./components/OptionsDataCenter').then(m => ({ default: m.OptionsDataCenter })));
const PortfolioOptimizationReport = lazyWithRetry(() => import('./components/PortfolioOptimizationReport').then(m => ({ default: m.PortfolioOptimizationReport })));
const PortfolioXRayReport = lazyWithRetry(() => import('./components/PortfolioXRayReport').then(m => ({ default: m.PortfolioXRayReport })));
const MajorThemeAnalysisWidget = lazyWithRetry(() => import('./components/MajorThemeAnalysisWidget').then(m => ({ default: m.MajorThemeAnalysisWidget })));
const MacroAnalysisView = lazyWithRetry(() => import('./components/DashboardWidgets').then(m => ({ default: m.MacroAnalysisView })));
const UpgradeModal = lazyWithRetry(() => import('./components/TierGate').then(m => ({ default: m.UpgradeModal })));
const AddonPaymentModal = lazyWithRetry(() => import('./components/TierGate').then(m => ({ default: m.AddonPaymentModal })));
const PaymentSuccessModal = lazyWithRetry(() => import('./components/PaymentSuccessModal').then(m => ({ default: m.PaymentSuccessModal })));
import { FinancialDisclaimerBanner } from './components/FinancialDisclaimerBanner';
import { MacroShockBanner } from './components/MacroShockBanner';

const ComponentLoadingFallback = () => (
  <div className="flex items-center justify-center p-12 w-full min-h-[200px]">
    <div className="relative w-8 h-8">
      <div className="absolute inset-0 border-2 border-emerald-500/20 rounded-full" />
      <div className="absolute inset-0 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  </div>
);

export const checkIsAdminEmail = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const adminEmailsStr = import.meta.env.VITE_ADMIN_EMAILS || 'lyrico220@gmail.com';
  const adminEmails = adminEmailsStr.toLowerCase().split(',').map((e: string) => e.trim());
  return adminEmails.includes(email.toLowerCase());
};

const checkIfAdmin = checkIsAdminEmail;

/** 탭별 접근에 필요한 최소 멤버십 등급 */
const TIER_TAB_ACCESS: Record<string, MembershipTier> = {
  
  simulator: 'TIER2',
  strategy: 'TIER3',
  calendar: 'TIER1',
};

function App() {
  const [showLoadingTimeout, setShowLoadingTimeout] = useState(false);
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    return (StorageManager.safeGet<string>('mmo_theme', 'dark') as 'dark' | 'light');
  });

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    StorageManager.safeSet('mmo_theme', newTheme);
    if (newTheme === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }
  }, [theme]);

  useEffect(() => {
    console.log('[MMO] App component mounting...');
  }, []);
  const [cspStatus, setCspStatus] = useState('...');
  const [sesStatus, setSesStatus] = useState('...');
  const [swStatus, setSwStatus] = useState('...');
  const [user, setUser] = useState<User | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'signup' | null>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const authParam = params.get('auth');
      if (authParam === 'login' || authParam === 'signup') return authParam;
      // 소셜 로그인 리다이렉트 콜백 (/auth/kakao/callback, /auth/naver/callback 또는 ?code= 감지 시 모달 자동 마운트)
      if (window.location.pathname.startsWith('/auth/') || params.has('code')) {
        const savedIntent = sessionStorage.getItem('auth_intent');
        return (savedIntent === 'signup' || savedIntent === 'login') ? savedIntent : 'login';
      }
    }
    return null;
  });
  const authModalModeRef = useRef(authModalMode);
  useEffect(() => {
    authModalModeRef.current = authModalMode;
  }, [authModalMode]);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'portfolio' | 'macro' | 'themes' | 'strategy' | 'options' | 'calendar' | 'history' | 'simulator' | 'guide' | 'settings' | 'news' | 'autotrading' | 'mypage'>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam && ['dashboard', 'portfolio', 'macro', 'themes', 'strategy', 'options', 'calendar', 'history', 'simulator', 'guide', 'settings', 'news', 'autotrading', 'mypage'].includes(tabParam)) {
      return tabParam as any;
    }
    return 'dashboard';
  });
  const [activeSimulateSymbol, setActiveSimulateSymbol] = useState<string | undefined>(undefined);
  const [simulatedAiTrades, setSimulatedAiTrades] = useState<MarketSignal[] | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    return StorageManager.safeGet<boolean>('mmo_sidebar_collapsed', false);
  });
  const toggleSidebarCollapsed = useCallback(() => {
    setIsSidebarCollapsed(prev => {
      const next = !prev;
      StorageManager.safeSet('mmo_sidebar_collapsed', next);
      return next;
    });
  }, []);
  const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(false);
  const [prefillSymbol, setPrefillSymbol] = useState('');
  const [prefillShares, setPrefillShares] = useState(0);
  const [prefillAvgCost, setPrefillAvgCost] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [addonConfig, setAddonConfig] = useState<any | null>(null);
  const [personalizationAddonConfig, setPersonalizationAddonConfig] = useState<any | null>(null);
  const [isChatPlusOpen, setIsChatPlusOpen] = useState(false);
  const [isAgentSubModalOpen, setIsAgentSubModalOpen] = useState(false);
  const [paymentSuccessModalData, setPaymentSuccessModalData] = useState<{ isOpen: boolean; tier: string; expiresAt?: string } | null>(null);

  useEffect(() => {
    const handleOpenAgentSub = () => setIsAgentSubModalOpen(true);
    window.addEventListener('app:open-agent-subscription', handleOpenAgentSub);
    return () => window.removeEventListener('app:open-agent-subscription', handleOpenAgentSub);
  }, []);


  const [isAdminPath, setIsAdminPath] = useState<boolean>(() => {
    return window.location.pathname.startsWith('/admin');
  });

  useEffect(() => {
    const handleLocationChange = () => {
      setIsAdminPath(window.location.pathname.startsWith('/admin'));
    };
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    if (activeTab !== 'portfolio') {
      setActiveSimulateSymbol(undefined);
    }
  }, [activeTab]);

  useEffect(() => {
    if (theme === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }
  }, [theme]);

  const [selectedStock, setSelectedStock] = useState<string | null>(null);
  const [selectedTechnicalSymbol, setSelectedTechnicalSymbol] = useState<string | null>(null);
  const [systemFeatures, setSystemFeatures] = useState<{
    featureDisabledMode: 'NOTICE' | 'HIDE';
    importScreenshotEnabled: boolean;
    addAssetEnabled: boolean;
    chatPlusCsEnabled: boolean;
    chatPlusTradingEnabled: boolean;
    quantSignalsEnabled: boolean;
    portfolioOptimizationEnabled: boolean;
    technicalAnalysisEnabled: boolean;
    brokerageSyncEnabled: boolean;
    liveOrderRoutingEnabled: boolean;
    aiAutonomousAgentEnabled: boolean;
    optionsDataCenterEnabled: boolean;
    aiPersonalizationEnabled: boolean;
  }>({
    featureDisabledMode: 'NOTICE',
    importScreenshotEnabled: true,
    addAssetEnabled: true,
    chatPlusCsEnabled: true,
    chatPlusTradingEnabled: true,
    quantSignalsEnabled: true,
    portfolioOptimizationEnabled: true,
    technicalAnalysisEnabled: true,
    brokerageSyncEnabled: true,
    liveOrderRoutingEnabled: true,
    aiAutonomousAgentEnabled: true,
    optionsDataCenterEnabled: true,
    aiPersonalizationEnabled: true
  });
  const [analysisType, setAnalysisType] = useState<'quantitative' | 'technical' | 'deep' | 'fundamental' | 'financial'>('quantitative');

  useEffect(() => {
    const fetchSystemFeatures = async () => {
      try {
        const data = await fetchSystemSecurityConfig();
        if (data) {
          setSystemFeatures({
            featureDisabledMode: data.featureDisabledMode === 'HIDE' ? 'HIDE' : 'NOTICE',
            importScreenshotEnabled: data.importScreenshotEnabled !== false,
            addAssetEnabled: data.addAssetEnabled !== false,
            chatPlusCsEnabled: data.chatPlusCsEnabled !== false,
            chatPlusTradingEnabled: data.chatPlusTradingEnabled !== false,
            quantSignalsEnabled: data.quantSignalsEnabled !== false,
            portfolioOptimizationEnabled: data.portfolioOptimizationEnabled !== false,
            technicalAnalysisEnabled: data.technicalAnalysisEnabled !== false,
            brokerageSyncEnabled: data.brokerageSyncEnabled !== false,
            liveOrderRoutingEnabled: data.liveOrderRoutingEnabled !== false,
            aiAutonomousAgentEnabled: data.aiAutonomousAgentEnabled !== false,
            optionsDataCenterEnabled: data.optionsDataCenterEnabled !== false,
            aiPersonalizationEnabled: data.aiPersonalizationEnabled !== false
          });
        }
      } catch (error) {
        console.error('Failed to load system features config:', error);
      }
    };
    fetchSystemFeatures();
  }, []);

  // Use both uid and id for compatibility with different auth/sync sources
  const effectiveId = user?.uid || (user as any)?.id || (isAuthReady && auth.currentUser ? auth.currentUser.uid : 'guest');

  const { apiKeys, saveApiKeys } = useSettings(effectiveId);

  // Immediate check on render
  if (sesStatus === '...') {
    if ((window as any).lockdown || (window as any).SES) setSesStatus('ACTIVE');
    else setSesStatus('NONE');
  }
  if (swStatus === '...') {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(regs => {
        setSwStatus(regs.length > 0 ? 'ACTIVE' : 'NONE');
      });
    } else {
      setSwStatus('UNSUPPORTED');
    }
  }


  const {
    holdings, rawHoldings, portfolioStats, macro, indicators, signals, aiAdvice, aiErrorInfo,
    isLoading, isIntelligenceLoading, lastUpdated, isModalOpen, setIsModalOpen,
    totalValue, dailyReturnAmt, dailyReturnPct, totalReturnAmt, totalReturnPct,
    proposedTrades, userProfile, setUserProfile, addHolding, addBulkHoldings, updateHolding,
    deleteHolding, updateTargetWeight, updateMultipleTargetWeights, fetchMarketData, fetchFullMacro,
    generateAiStrategy, generateQuickSummary, isAnalysisRunning, isDeepAnalysisRunning, analysisProgress,
    analysisStage, fetchDeepIntelligence, fetchIntelligenceOnly, stopAiAnalysis, macroReportFormat,
    setMacroReportFormat, isMacroLoading, isInitialLoadDone, refreshProfile,
    displayCurrencyPreference, setCurrencyPreference, changeMarketMode, isDbError,
    optimizationData, isOptimizing, optimizationStage, optimizationProgress, runOptimization,
    currentExchangeRate, liquidateHolding,
    watchlist, addWatchlist, removeWatchlist,
    selectedAccountId, setSelectedAccountId,
    saveTradeExecution,
  } = usePortfolioData(effectiveId);

  // 구독 모달 등에서 구독 성공 후 즉시 profile 갱신
  useEffect(() => {
    const handleRefreshProfile = () => { refreshProfile(); };
    window.addEventListener('app:refresh-profile', handleRefreshProfile);
    return () => window.removeEventListener('app:refresh-profile', handleRefreshProfile);
  }, [refreshProfile]);

  // 실거래(Private Terminal) 모드 단일 진실원 — 실거래 호스트 + 백엔드 권한 플래그(canAccessLiveTrading) 기반
  const isLiveMode = useLiveTradingMode(userProfile);
  const { getFeatureName } = useTerminology(userProfile?.uiLanguage, isLiveMode);

  useEffect(() => {
    const isKo = userProfile?.uiLanguage !== 'en';

    const TAB_TITLES_KO: Record<string, string> = {
      dashboard: '대시보드',
      portfolio: '포트폴리오',
      macro: '매크로 분석',
      themes: '테마 분석',
      strategy: '투자 전략',
      options: '옵션 체인',
      calendar: '포트폴리오 캘린더',
      history: '성과 & 기록',
      simulator: '전략 시뮬레이터',
      guide: '사용자 가이드',
      settings: '설정',
      news: '증시속보 & 임팩트',
      autotrading: isLiveMode ? '스마트 주문' : '시그널 레이더',
      mypage: '마이페이지'
    };

    const TAB_TITLES_EN: Record<string, string> = {
      dashboard: 'Dashboard',
      portfolio: 'Portfolio',
      macro: 'Macro Intelligence',
      themes: 'Theme Intelligence',
      strategy: 'Strategic Allocation',
      options: 'Option Chain',
      calendar: 'Portfolio Calendar',
      history: 'History & Performance',
      simulator: 'Simulator Hub',
      guide: 'User Guide',
      settings: 'Settings',
      news: 'News Impact',
      autotrading: isLiveMode ? 'Smart Orders' : 'Signal Radar',
      mypage: 'My Page'
    };

    const tabName = (isKo ? TAB_TITLES_KO[activeTab] : TAB_TITLES_EN[activeTab]) || activeTab;

    if (isLiveMode) {
      document.title = `EZOptix Pro | ${tabName} - Private Terminal`;
    } else {
      document.title = `EZOptix | ${tabName} - 모의투자 (Sandbox)`;
    }
  }, [activeTab, isLiveMode, userProfile?.uiLanguage]);

  const handleRunOptimization = useCallback(() => {
    if (!systemFeatures.portfolioOptimizationEnabled) {
      showToast(
        userProfile?.uiLanguage === 'ko'
          ? '포트폴리오 최적화 기능이 관리자에 의해 비활성화되었습니다.'
          : 'Portfolio Optimization is disabled by administrator.',
        'error'
      );
      return;
    }
    runOptimization();
  }, [runOptimization, systemFeatures.portfolioOptimizationEnabled, userProfile]);

  const isAddonActive = userProfile?.activeAddons?.some(a => a.id === 'CHAT_PREDICTION_ADDON') || userProfile?.role === 'ADMIN' || userProfile?.role === 'SUPER_ADMIN';



  const [deviceLimitExceededInfo, setDeviceLimitExceededInfo] = useState<any>(null);
  const [upgradeModalConfig, setUpgradeModalConfig] = useState<{
    requiredTier: MembershipTier;
    featureName: string;
    billingCycle?: 'monthly' | 'annual';
  } | null>(null);

  const [addonPaymentModalConfig, setAddonPaymentModalConfig] = useState<{
    addonId: string;
  } | null>(null);

  const {
    tier, limits, checkFeature, checkUsageLimit, remainingUsage,
    canAddHolding, canAddWatchlist, hasAccess, nextTier, isExpired, tierLabel,
    hasAutoTradingAccess
  } = useTierGate(userProfile);

  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);

  // Single Source of Truth: Admin Console(DB) 기반 가격 조회
  // 직접 하드코딩 금지 - fetchTierPriceFromServer()를 통해 항상 서버 API에서 가격 로딩
  const [serverProductsCache, setServerProductsCache] = useState<any>(null);
  useEffect(() => {
    // 컴포넌트 마운트 시 한 번 products config 로드
    fetch('/api/payment/products')
      .then(res => res.json())
      .then((data: any) => {
        if (data && data.products) {
          setServerProductsCache(data);
        }
      })
      .catch(err => console.error("[Products] Failed to fetch products config:", err));
  }, []);

  const isAddonProduct = useCallback((id: string): boolean => {
    if (!id || typeof id !== 'string') return false;
    return [
      'OPTIONS_ANALYTICS_ADDON',
      'addon_agent_standard',
      'addon_agent_pro',
      'addon_agent_vip',
      'addon_smart_order_standard',
      'addon_smart_order_pro',
      'addon_smart_order_vip',
      'AI_PERSONALIZATION_ADDON',
      'CHAT_PREDICTION_ADDON',
      'API_ACCESS_ADDON',
    ].includes(id) || id.startsWith('addon_') || id.endsWith('_ADDON');
  }, []);

  const handleUpgradeOrAddonRequest = useCallback((
    requiredTier: string,
    featureName?: string,
    billingCycle?: 'monthly' | 'annual'
  ) => {
    if (isAddonProduct(requiredTier)) {
      setAddonPaymentModalConfig({ addonId: requiredTier });
    } else {
      const validTier: MembershipTier = (['TIER1', 'TIER2', 'TIER3', 'TIER4', 'TIER5'].includes(requiredTier as any))
        ? (requiredTier as MembershipTier)
        : 'TIER3';
      setUpgradeModalConfig({
        requiredTier: validTier,
        featureName: featureName || (userProfile?.uiLanguage === 'ko' ? 'AI 전략 분석' : 'AI Strategic Analysis'),
        billingCycle
      });
    }
  }, [isAddonProduct, userProfile?.uiLanguage]);

  useEffect(() => {
    const handleOpenUpgradeModal = (e: CustomEvent) => {
      const { requiredTier, featureName, billingCycle } = e.detail || {};
      handleUpgradeOrAddonRequest(requiredTier, featureName, billingCycle);
    };
    window.addEventListener('open:upgrade-modal' as any, handleOpenUpgradeModal);
    return () => window.removeEventListener('open:upgrade-modal' as any, handleOpenUpgradeModal);
  }, [handleUpgradeOrAddonRequest]);

  const PRODUCT_FALLBACK_PRICES_KRW: Record<string, { monthly: number; annual: number }> = {
    TIER1: { monthly: 0, annual: 0 },
    TIER2: { monthly: 9900, annual: 99000 },
    TIER3: { monthly: 24900, annual: 249000 },
    TIER4: { monthly: 49000, annual: 490000 },
    TIER5: { monthly: 89000, annual: 890000 },
    addon_agent_standard: { monthly: 29000, annual: 290000 },
    addon_agent_pro: { monthly: 89000, annual: 890000 },
    addon_agent_vip: { monthly: 299000, annual: 2990000 },
    addon_smart_order_standard: { monthly: 29000, annual: 290000 },
    addon_smart_order_pro: { monthly: 89000, annual: 890000 },
    addon_smart_order_vip: { monthly: 299000, annual: 2990000 },
    OPTIONS_ANALYTICS_ADDON: { monthly: 24900, annual: 249000 },
    AI_PERSONALIZATION_ADDON: { monthly: 9900, annual: 99000 },
    CHAT_PREDICTION_ADDON: { monthly: 12900, annual: 129000 },
    API_ACCESS_ADDON: { monthly: 19900, annual: 199000 },
  };

  const getTierPriceFromConfig = (tierKey: string, bCycle: 'monthly' | 'annual' = 'monthly'): number => {
    const isAnnual = bCycle === 'annual';
    const products = serverProductsCache?.products;
    if (products && products[tierKey]) {
      const p = products[tierKey];
      const krwVal = p.priceKRW ?? p.price_krw ?? (p.currency === 'KRW' ? p.price : undefined);
      const usdVal = p.priceUSD ?? p.price_usd ?? (p.currency === 'USD' ? p.price : undefined);
      const annKrwVal = p.annualPriceKRW ?? p.annual_price_krw;

      let val = 0;
      if (isAnnual) {
        val = Number(annKrwVal) || Number(krwVal) * 10 || Number(usdVal) * 10000 || 0;
      } else {
        val = Number(krwVal) || Number(usdVal) * 1000 || 0;
      }
      if (!isNaN(val) && val > 0) return val;
    }

    const addOns = serverProductsCache?.addOns;
    if (Array.isArray(addOns)) {
      const foundAddon = addOns.find((a: any) => a.id === tierKey);
      if (foundAddon && Number(foundAddon.price) > 0) {
        return isAnnual ? Number(foundAddon.price) * 10 : Number(foundAddon.price);
      }
    }

    const fallback = PRODUCT_FALLBACK_PRICES_KRW[tierKey];
    if (fallback) {
      return isAnnual ? fallback.annual : fallback.monthly;
    }
    return 0;
  };

  const getTierPriceKRW = (tierKey: string, bCycle: 'monthly' | 'annual' = 'monthly') => {
    const price = getTierPriceFromConfig(tierKey, bCycle);
    if (price > 0) return price;
    const fallback = PRODUCT_FALLBACK_PRICES_KRW[tierKey];
    if (fallback) {
      return bCycle === 'annual' ? fallback.annual : fallback.monthly;
    }
    return 0;
  };

  const handlePayment = useCallback(async (
    targetProduct: MembershipTier | 'OPTIONS_ANALYTICS_ADDON' | 'AI_PERSONALIZATION_ADDON' | 'CHAT_PREDICTION_ADDON' | 'API_ACCESS_ADDON' | 'addon_agent_standard' | 'addon_agent_pro' | 'addon_agent_vip' | 'addon_smart_order_standard' | 'addon_smart_order_pro' | 'addon_smart_order_vip',
    useExistingCard?: boolean,
    billingCycle: 'monthly' | 'annual' = 'monthly',
    paymentType: 'subscription' | 'onetime' = 'subscription'
  ) => {
    if (effectiveId === 'guest') {
      showToast(
        userProfile?.uiLanguage === 'ko'
          ? '게스트 계정에서는 결제 기능을 이용하실 수 없습니다. 로그인 후 이용해주세요.'
          : 'Payment is not available for guest accounts. Please sign in.',
        'error'
      );
      return;
    }

    const displayNameMap: Record<string, string> = {
      addon_agent_standard: userProfile?.uiLanguage === 'ko' ? '실시간 시그널 웹훅 Standard' : 'Signal Webhook Standard',
      addon_agent_pro: userProfile?.uiLanguage === 'ko' ? '고급 퀀트 데이터 피드 & 웹훅 Pro' : 'Quant Webhook & Data Feed Pro',
      addon_agent_vip: userProfile?.uiLanguage === 'ko' ? '엔터프라이즈 퀀트 시뮬레이션 VIP' : 'Enterprise Quant Simulation VIP',
      addon_smart_order_standard: userProfile?.uiLanguage === 'ko' ? '스마트 주문 Standard (Smart Order Standard)' : 'Smart Order Standard',
      addon_smart_order_pro: userProfile?.uiLanguage === 'ko' ? '스마트 주문 Pro (Smart Order Pro)' : 'Smart Order Pro',
      addon_smart_order_vip: userProfile?.uiLanguage === 'ko' ? '스마트 주문 VIP (Smart Order VIP)' : 'Smart Order VIP',
      OPTIONS_ANALYTICS_ADDON: getFeatureName('OPTIONS_ANALYTICS'),
      AI_PERSONALIZATION_ADDON: getFeatureName('AI_PERSONALIZATION'),
      CHAT_PREDICTION_ADDON: getFeatureName('CHAT_PLUS'),
      API_ACCESS_ADDON: getFeatureName('API_ACCESS'),
      TIER2: 'Tier 2 (새싹 투자자) 멤버십',
      TIER3: 'Tier 3 (시장 참여자) 멤버십',
      TIER4: 'Tier 4 (전략가) 멤버십',
      TIER5: 'Tier 5 (프로) 멤버십'
    };
    const orderName = displayNameMap[targetProduct] || '구독 상품';
    const amount = getTierPriceKRW(targetProduct, billingCycle);

    if (amount <= 0) {
      showToast(
        userProfile?.uiLanguage === 'ko'
          ? '유효한 결제 금액을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.'
          : 'Failed to load valid payment amount. Please try again later.',
        'error'
      );
      return;
    }

    const isAddonProduct = targetProduct.startsWith('addon_') || targetProduct.includes('_ADDON');

    if (paymentType === 'onetime') {
      setIsPaymentProcessing(true);
      try {
        const res = await requestPortOnePayment({
          tier: targetProduct,
          orderName: billingCycle === 'annual' ? `${orderName} 1년 (일회성)` : `${orderName} 30일권`,
          amount,
          billingCycle,
          isAddon: isAddonProduct,
          customerName: userProfile?.displayName,
          customerEmail: userProfile?.email
        });

        if (res.success) {
          await refreshProfile();
          setIsAgentSubModalOpen(false);
          setAddonPaymentModalConfig(null);
          setActiveTab('mypage');
          setPaymentSuccessModalData({ isOpen: true, tier: targetProduct, expiresAt: res.expiresAt });
          showToast(
            userProfile?.uiLanguage === 'ko'
              ? '성공적으로 결제가 완료되었습니다!'
              : 'Payment completed successfully!',
            'success'
          );
        } else {
          showToast(res.error || '결제가 취소되었거나 실패했습니다.', 'error');
        }
      } catch (e: any) {
        showToast(`결제 오류: ${e.message || e}`, 'error');
      } finally {
        setIsPaymentProcessing(false);
      }
      return;
    }

    const userTier = userProfile?.membershipTier || 'TIER1';
    if (targetProduct === 'addon_agent_pro') {
      const minRequired = 'TIER3';
      if (!canAccessTier(userTier, minRequired)) {
        const errorMsg = userProfile?.uiLanguage === 'ko'
          ? `이 부가상품은 Tier 3 (시장 참여자) 등급 이상만 구독할 수 있습니다. 먼저 등급을 올려주세요.`
          : `This add-on requires Tier 3 (Intermediate) tier or higher. Please upgrade first.`;
        alert(errorMsg);
        return;
      }
    }
    if (targetProduct === 'OPTIONS_ANALYTICS_ADDON') {
      const minRequired = 'TIER4';
      if (!canAccessTier(userTier, minRequired)) {
        const errorMsg = userProfile?.uiLanguage === 'ko'
          ? `이 부가상품은 Tier 4 (전략가) 등급 이상 권장(필수) 상품입니다. 먼저 등급을 올려주세요.`
          : `This add-on requires Tier 4 (Strategist) tier or higher. Please upgrade first.`;
        alert(errorMsg);
        return;
      }
    }
    if (targetProduct === 'AI_PERSONALIZATION_ADDON') {
      const minRequired = 'TIER5';
      if (!canAccessTier(userTier, minRequired)) {
        const errorMsg = userProfile?.uiLanguage === 'ko'
          ? `이 부가상품은 Tier 5 (프로) 등급 이상만 구독할 수 있습니다. 먼저 등급을 올려주세요.`
          : `This add-on requires Tier 5 (Pro) tier or higher. Please upgrade first.`;
        alert(errorMsg);
        return;
      }
    }
    if (targetProduct === 'CHAT_PREDICTION_ADDON') {
      const minRequired = 'TIER4';
      if (!canAccessTier(userTier, minRequired)) {
        const errorMsg = userProfile?.uiLanguage === 'ko'
          ? `이 부가상품은 Tier 4 (전략가) 등급 이상만 구독할 수 있습니다. 먼저 등급을 올려주세요.`
          : `This add-on requires Tier 4 (Advanced) tier or higher. Please upgrade first.`;
        alert(errorMsg);
        return;
      }
    }
    if (targetProduct === 'API_ACCESS_ADDON') {
      const minRequired = 'TIER5';
      if (!canAccessTier(userTier, minRequired)) {
        const errorMsg = userProfile?.uiLanguage === 'ko'
          ? `이 부가상품은 Tier 5 (프로) 등급 이상만 구독할 수 있습니다. 먼저 등급을 올려주세요.`
          : `This add-on requires Tier 5 (Pro) tier or higher. Please upgrade first.`;
        alert(errorMsg);
        return;
      }
    }

    // 포트원(PortOne) V2 정기결제 빌링키 발급 및 첫 회 결제 처리
    setIsPaymentProcessing(true);
    try {
      const res = await requestPortOneSubscription({
        tier: targetProduct,
        orderName: `${orderName} 정기구독`,
        amount,
        billingCycle,
        isAddon: isAddonProduct,
        customerName: userProfile?.displayName,
        customerEmail: userProfile?.email
      });

      if (res.success) {
        await refreshProfile();
        setIsAgentSubModalOpen(false);
        setAddonPaymentModalConfig(null);
        setActiveTab('mypage');
        setPaymentSuccessModalData({ isOpen: true, tier: targetProduct, expiresAt: res.expiresAt });
        showToast(
          userProfile?.uiLanguage === 'ko'
            ? '성공적으로 정기 구독 신청이 완료되었습니다!'
            : 'Subscription successfully completed!',
          'success'
        );
      } else {
        showToast(res.error || '정기결제 카드 등록이 취소되었습니다.', 'error');
      }
    } catch (e: any) {
      console.error("PortOne subscription failed:", e);
      showToast(
        userProfile?.uiLanguage === 'ko'
          ? `정기결제 오류: ${e.message || e}`
          : `Subscription error: ${e.message || e}`,
        'error'
      );
    } finally {
      setIsPaymentProcessing(false);
    }
  }, [effectiveId, userProfile, addonConfig, personalizationAddonConfig, showToast, refreshProfile]);

  const processedPaymentKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!effectiveId || effectiveId === 'guest') return;

    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const billingStatus = urlParams.get('billing');

    if (paymentStatus === 'success') {
      const paymentKey = urlParams.get('paymentKey');
      const orderId = urlParams.get('orderId');
      const amount = urlParams.get('amount');
      const targetTier = urlParams.get('tier') || 'PRO';

      if (paymentKey && orderId && amount) {
        if (processedPaymentKeysRef.current.has(paymentKey)) {
          return;
        }
        processedPaymentKeysRef.current.add(paymentKey);
        window.history.replaceState({}, document.title, window.location.pathname);

        const confirmPayment = async () => {
          setIsPaymentProcessing(true);
          try {
            const token = await auth.currentUser?.getIdToken();
            if (!token) throw new Error('인증 토큰을 가져오지 못했습니다. 다시 로그인해주세요.');

            const res = await fetch('/api/payment/confirm', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                paymentKey,
                orderId,
                amount: Number(amount),
                tier: targetTier
              })
            });

            const data = await res.json() as any;
            if (!res.ok) {
              throw new Error(data.error || '결제 승인 중 오류가 발생했습니다.');
            }

            await refreshProfile();
            setActiveTab('mypage');
            setIsAgentSubModalOpen(false);
            setAddonPaymentModalConfig(null);
            setPaymentSuccessModalData({ isOpen: true, tier: targetTier, expiresAt: data.expiresAt });

            if (targetTier === 'OPTIONS_ANALYTICS_ADDON') {
              showToast(
                userProfile?.uiLanguage === 'ko'
                  ? `성공적으로 스마트머니 옵션/공매도 분석 부가상품이 활성화되었습니다!`
                  : `Successfully subscribed to Options & Short Selling Analytics add-on!`,
                'success'
              );
            } else if (targetTier === 'addon_agent_pro') {
              showToast(
                userProfile?.uiLanguage === 'ko'
                  ? `성공적으로 고급 퀀트 데이터 피드 & 웹훅 Pro 부가상품이 활성화되었습니다!`
                  : `Successfully subscribed to Quant Webhook & Data Feed Pro add-on!`,
                'success'
              );
            } else if (targetTier === 'AI_PERSONALIZATION_ADDON') {
              showToast(
                userProfile?.uiLanguage === 'ko'
                  ? `성공적으로 AI 개인화 부가상품이 활성화되었습니다!`
                  : `Successfully subscribed to AI Personalization add-on!`,
                'success'
              );
            } else if (targetTier === 'CHAT_PREDICTION_ADDON') {
              showToast(
                userProfile?.uiLanguage === 'ko'
                  ? `성공적으로 AI 챗 플러스 퀀트 분석기 부가상품이 활성화되었습니다!`
                  : `Successfully subscribed to AI Chat Plus Quant Analytics add-on!`,
                'success'
              );
            } else if (targetTier === 'API_ACCESS_ADDON') {
              showToast(
                userProfile?.uiLanguage === 'ko'
                  ? `성공적으로 Developer JSON Raw Data API 부가상품이 활성화되었습니다!`
                  : `Successfully subscribed to Developer JSON Raw Data API add-on!`,
                'success'
              );
            } else {
              showToast(
                userProfile?.uiLanguage === 'ko'
                  ? `성공적으로 ${targetTier} 등급으로 업그레이드되었습니다!`
                  : `Successfully upgraded to ${targetTier} membership!`,
                'success'
              );
            }
          } catch (err: any) {
            console.error("Payment confirmation failed:", err);
            const errorMsg = err.message || '결제 승인에 실패했습니다.';
            alert(`결제 승인이 거절되었습니다.\n\n사유: ${errorMsg}\n\n(승인되지 않은 결제는 자동으로 취소 및 환불 처리됩니다.)`);
            showToast(errorMsg, 'error');
          } finally {
            setIsPaymentProcessing(false);
          }
        };
        confirmPayment();
      }
    } else if (paymentStatus === 'fail') {
      const code = urlParams.get('code');
      const message = urlParams.get('message') || '결제가 취소되었습니다.';
      showToast(`결제 실패: ${message} (코드: ${code})`, 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (billingStatus === 'success') {
      const authKey = urlParams.get('authKey');
      const customerKey = urlParams.get('customerKey');
      const targetTier = urlParams.get('tier') || 'PRO';
      const billingCycle = urlParams.get('billingCycle') || 'monthly';

      if (authKey && customerKey) {
        if (processedPaymentKeysRef.current.has(authKey)) {
          return;
        }
        processedPaymentKeysRef.current.add(authKey);
        window.history.replaceState({}, document.title, window.location.pathname);

        const confirmBilling = async () => {
          setIsPaymentProcessing(true);
          try {
            const token = await auth.currentUser?.getIdToken();
            if (!token) throw new Error('인증 토큰을 가져오지 못했습니다. 다시 로그인해주세요.');

            const res = await fetch('/api/payment/billing/confirm', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                authKey,
                customerKey,
                tier: targetTier,
                billingCycle
              })
            });

            const data = await res.json() as any;
            if (!res.ok) {
              throw new Error(data.error || '결제 승인 중 오류가 발생했습니다.');
            }

            await refreshProfile();
            setActiveTab('mypage');
            setIsAgentSubModalOpen(false);
            setAddonPaymentModalConfig(null);
            setPaymentSuccessModalData({ isOpen: true, tier: targetTier, expiresAt: data.expiresAt });

            showToast(
              userProfile?.uiLanguage === 'ko'
                ? `정기결제 카드가 성공적으로 등록되었으며 결제가 정상 처리되었습니다.`
                : `Billing card registered successfully and subscription payment processed.`,
              'success'
            );
            setIsPaymentProcessing(false);
          } catch (err: any) {
            console.error("Billing confirmation failed:", err);
            showToast(err.message || '카드 등록 및 정기 결제 승인에 실패했습니다.', 'error');
            setIsPaymentProcessing(false);
          }
        };
        confirmBilling();
      }
    } else if (billingStatus === 'fail') {
      const code = urlParams.get('code');
      const message = urlParams.get('message') || '결제 카드 등록이 취소되었습니다.';
      showToast(`카드 등록 실패: ${message} (코드: ${code})`, 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [effectiveId, refreshProfile, userProfile]);

  const [portfolioTab, setPortfolioTab] = useState<'holdings' | 'watchlist'>('holdings');
  const [portfolioMainSection, setPortfolioMainSection] = useState<'holdings' | 'signals' | 'optimization'>('holdings');
  const [optSubTab, setOptSubTab] = useState<'ai' | 'quant' | 'xray'>('ai');

  const handleUpdateTargetWeight = useCallback(async (symbol: string, weight: number) => {
    try {
      await updateTargetWeight(symbol, weight);
      showToast(userProfile?.uiLanguage === 'ko'
        ? `${symbol}의 목표 비중이 ${(weight * 100).toFixed(0)}%로 변경되었습니다.`
        : `${symbol} target weight updated to ${(weight * 100).toFixed(0)}%.`
      );
    } catch (e) {
      console.error("Failed to update target weight", e);
      showToast(userProfile?.uiLanguage === 'ko' ? '목표 비중 변경 중 오류가 발생했습니다.' : 'Failed to update target weight.', 'error');
    }
  }, [updateTargetWeight, showToast, userProfile?.uiLanguage]);

  const [userAccounts, setUserAccounts] = useState<any[]>([]);

  const refreshAccountsList = useCallback(async () => {
    if (!effectiveId || effectiveId === 'guest') return;
    try {
      const { fetchUserAccounts } = await import('./services/api');
      const accounts = await fetchUserAccounts();
      setUserAccounts(accounts);
      setSelectedAccountId(prev => {
        if (prev !== 'all' && !accounts.some((a: any) => a.id === prev)) {
          return 'all';
        }
        return prev;
      });
    } catch (e) {
      console.error("Failed to fetch user accounts list", e);
    }
  }, [effectiveId, setSelectedAccountId]);

  const [paperExecutionMode, setPaperExecutionMode] = useState<'VIRTUAL_SANDBOX' | 'BROKER_PAPER'>('VIRTUAL_SANDBOX');

  const refreshTradingConfig = useCallback(async () => {
    if (!effectiveId || effectiveId === 'guest') return;
    try {
      const { fetchUserTradingConfig } = await import('./services/api');
      const cfg = await fetchUserTradingConfig();
      if (cfg?.paper_execution_mode) {
        setPaperExecutionMode(cfg.paper_execution_mode);
      }
    } catch {}
  }, [effectiveId]);

  useEffect(() => {
    refreshTradingConfig();
    const handleCfgUpdate = (e: any) => {
      if (e?.detail?.paperExecutionMode) {
        setPaperExecutionMode(e.detail.paperExecutionMode);
      } else {
        refreshTradingConfig();
      }
      refreshAccountsList();
    };
    window.addEventListener('trading-config:updated', handleCfgUpdate);
    window.addEventListener('app:refresh', handleCfgUpdate);
    return () => {
      window.removeEventListener('trading-config:updated', handleCfgUpdate);
      window.removeEventListener('app:refresh', handleCfgUpdate);
    };
  }, [refreshTradingConfig, refreshAccountsList]);

  useEffect(() => {
    if (user && userProfile && userProfile.isApproved && (activeTab === 'portfolio' || activeTab === 'dashboard' || activeTab === 'settings' || activeTab === 'simulator')) {
      refreshAccountsList();
    }
  }, [user, userProfile, isLiveMode, activeTab, refreshAccountsList]);

  useEffect(() => {
    const handleAppRefresh = () => {
      refreshAccountsList();
    };
    window.addEventListener('app:refresh', handleAppRefresh);
    return () => window.removeEventListener('app:refresh', handleAppRefresh);
  }, [refreshAccountsList]);

  const [portfolioMarketFilter, setPortfolioMarketFilter] = useState<'ALL' | 'US' | 'KR'>('ALL');

  // Synchronize dashboard market mode to portfolio grid filter when targetMarket changes
  useEffect(() => {
    if (userProfile?.targetMarket) {
      setPortfolioMarketFilter(userProfile.targetMarket);
    }
  }, [userProfile?.targetMarket]);

  const handlePortfolioMarketFilterChange = useCallback((filter: 'ALL' | 'US' | 'KR') => {
    setPortfolioMarketFilter(filter);
  }, []);

  const portfolioGroupData = useMemo(() => {
    if (!portfolioStats) return null;
    return portfolioStats[portfolioMarketFilter];
  }, [portfolioStats, portfolioMarketFilter]);

  const currentPortfolioValue = portfolioGroupData?.totalValue ?? totalValue;
  const currentPortfolioDailyAmt = portfolioGroupData?.dailyReturnAmt ?? dailyReturnAmt;
  const currentPortfolioDailyPct = portfolioGroupData?.dailyReturnPct ?? dailyReturnPct;
  const currentPortfolioTotalAmt = portfolioGroupData?.totalReturnAmt ?? totalReturnAmt;
  const currentPortfolioTotalPct = portfolioGroupData?.totalReturnPct ?? totalReturnPct;
  const currentPortfolioHoldings = portfolioGroupData?.enrichedHoldings ?? holdings;

  const currentPortfolioSignals = useMemo(() => {
    let filtered = signals;
    if (portfolioMarketFilter !== 'ALL') {
      filtered = filtered.filter(sig => {
        const assetMarket = getAssetMarket(sig.symbol);
        return assetMarket === portfolioMarketFilter;
      });
    }

    if (portfolioTab === 'holdings') {
      const holdingSymbols = new Set(
        holdings
          .filter(h => h.shares > 0)
          .map(h => h.symbol.toUpperCase())
      );
      return filtered.filter(sig => holdingSymbols.has(sig.symbol.toUpperCase()));
    } else {
      const watchlistSymbols = new Set(watchlist.map(w => w.symbol.toUpperCase()));
      return filtered.filter(sig => watchlistSymbols.has(sig.symbol.toUpperCase()));
    }
  }, [signals, portfolioMarketFilter, portfolioTab, holdings, watchlist]);

  const quickScanAttemptRef = React.useRef<{ indicators: any; aiSummary: string | null }>({ indicators: null, aiSummary: null });

  const generateQuickSummaryRef = React.useRef(generateQuickSummary);
  useEffect(() => {
    generateQuickSummaryRef.current = generateQuickSummary;
  }, [generateQuickSummary]);

  // Dynamically resolve target goal based on current display currency preference and exchange rate
  const targetGoal = useMemo(() => {
    const goalUS = userProfile?.targetGoalAmountUS || 0;
    const goalKR = userProfile?.targetGoalAmountKR || 0;

    if (portfolioMarketFilter === 'ALL') {
      const unifiedGoalKRW = (goalUS * currentExchangeRate) + goalKR;
      if (displayCurrencyPreference === 'USD') {
        return unifiedGoalKRW / currentExchangeRate;
      }
      return unifiedGoalKRW;
    } else if (portfolioMarketFilter === 'US') {
      if (displayCurrencyPreference === 'KRW') {
        return goalUS * currentExchangeRate;
      }
      return goalUS;
    } else { // KR
      if (displayCurrencyPreference === 'USD') {
        return goalKR / currentExchangeRate;
      }
      return goalKR;
    }
  }, [userProfile, portfolioMarketFilter, displayCurrencyPreference, currentExchangeRate]);

  // Automatically update currency preference when portfolio market filter changes
  useEffect(() => {
    if (portfolioMarketFilter === 'ALL' || portfolioMarketFilter === 'KR') {
      setCurrencyPreference('KRW');
    } else if (portfolioMarketFilter === 'US') {
      setCurrencyPreference('USD');
    }
  }, [portfolioMarketFilter, setCurrencyPreference]);

  const handleToggleTrendWatchlist = useCallback(async (symbol: string, name?: string, market?: string) => {
    const targetMarket = market || (symbol.endsWith('.KS') || symbol.endsWith('.KQ') ? 'KR' : 'US');
    const isListed = watchlist.some(w => w.symbol.toUpperCase() === symbol.toUpperCase());
    if (isListed) {
      await removeWatchlist(symbol, targetMarket);
      showToast(userProfile?.uiLanguage === 'ko'
        ? `${name || symbol}이(가) 관심종목에서 삭제되었습니다.`
        : `${name || symbol} removed from watchlist.`
      );
    } else {
      if (!canAddWatchlist(watchlist.length)) {
        return;
      }
      await addWatchlist(symbol, name || symbol, targetMarket, isLiveMode ? '트렌드 추천' : '트렌드 모멘텀');
      showToast(userProfile?.uiLanguage === 'ko'
        ? `${name || symbol}이(가) 관심종목에 등록되었습니다.`
        : `${name || symbol} added to watchlist.`
      );
    }
  }, [watchlist, removeWatchlist, showToast, userProfile?.uiLanguage, canAddWatchlist, addWatchlist]);

  const handleStartAiSimulation = useCallback((tradesToSimulate?: MarketSignal[]) => {
    if (tradesToSimulate && tradesToSimulate.length > 0) {
      setSimulatedAiTrades(tradesToSimulate);
    } else if (proposedTrades && proposedTrades.length > 0) {
      setSimulatedAiTrades(proposedTrades);
    } else {
      setSimulatedAiTrades(null);
    }
    setActiveTab('simulator');
  }, [proposedTrades]);

  const handleStartSimulationWithRecommendations = useCallback((recs: any[]) => {
    if (!recs || recs.length === 0) {
      handleStartAiSimulation(proposedTrades);
      return;
    }

    const recSignals: MarketSignal[] = recs.map(rec => {
      const weightPct = parseFloat(rec.weight.replace(/[^0-9.-]/g, '')) || 0;
      const priceRaw = rec.buy.replace(/[^0-9.-]/g, '');
      const price = parseFloat(priceRaw) || 100;

      const targetVal = totalValue * (weightPct / 100);
      const shares = Math.round(targetVal / price) || 0;

      return {
        symbol: rec.ticker.toUpperCase(),
        name: rec.name || rec.ticker,
        price: price,
        prevClose: price,
        pctChange: 0,
        shortRatio: 0,
        daysToCover: 0,
        putCallRatio: 0,
        ivRank: 0,
        gammaFlip: 0,
        maxPain: 0,
        totalScore: 50,
        alertLevel: 'NORMAL',
        portfolioAction: 'ADD',
        marketState: 'REGULAR',
        market: (portfolioMarketFilter === 'ALL' ? 'US' : portfolioMarketFilter) as 'US' | 'KR',
        proposedTradeShares: shares,
        buyTargetPrice: price
      };
    });

    const combinedTrades = [...(proposedTrades || [])];
    recSignals.forEach(recSig => {
      const existingIdx = combinedTrades.findIndex(t => t.symbol.toUpperCase() === recSig.symbol.toUpperCase());
      if (existingIdx >= 0) {
        combinedTrades[existingIdx] = recSig;
      } else {
        combinedTrades.push(recSig);
      }
    });

    handleStartAiSimulation(combinedTrades);
  }, [proposedTrades, totalValue, portfolioMarketFilter, handleStartAiSimulation]);


  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      console.log("[App] Auth state changed. Firebase User:", firebaseUser?.email || "None");

      if (firebaseUser) {
        // Always refresh token on auth state change
        firebaseUser.getIdToken().then(token => {
          localStorage.setItem('mmo_token', token);
        });

        // If we have a firebase user but no local 'user' state yet
        if (!user) {
          // If the auth modal is actively open, let AuthView complete its validation and call onLoginSuccess!
          if (authModalModeRef.current) {
            console.log("[App] Auth modal is active. Letting AuthView handle validation before setting user.");
            return;
          }

          // If this is a returning session (not a fresh redirect login), set user immediately
          // Check if there is NO redirect result being processed (no firebase query params)
          if (!window.location.search.includes('__firebase_request_key')) {
            console.log("[App] Normal session detected, setting user immediately.");
            // StorageManager: localStorage 버전 마이그레이션 실행
            StorageManager.init(firebaseUser.uid);
            setUser(firebaseUser);
          } else {
            console.log("[App] Redirect flow detected, letting AuthView handle sync...");
            // Safety timeout: if AuthView doesn't sync in 5 seconds, force it
            setTimeout(() => {
              if (!user && auth.currentUser && !authModalModeRef.current) {
                console.warn("[App] Auth sync taking too long, forcing state...");
                StorageManager.init(auth.currentUser.uid);
                setUser(auth.currentUser);
              }
            }, 5000);
          }
        }
      } else {
        if (user) {
          // StorageManager: 로그아웃 시 민감 정보 및 채팅 캐시 클리어
          StorageManager.clearSensitive(user.uid);
          // 서버 측 세션 대화 내역 정리 (best-effort)
          user.getIdToken().then((token: string) => {
            fetch('/api/chat/history', {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            }).catch(() => {});
          }).catch(() => {});
        }
        setUser(null);
        localStorage.removeItem('mmo_token');
      }
      setIsAuthReady(true);
    });

    // Capacitor 환경에서 Firebase 초기화가 무한 대기(Hang)되는 버그 방지용 안전 장치
    const safetyTimer = setTimeout(() => {
      setIsAuthReady(prev => {
        if (!prev) console.warn("[App] Auth state change timed out. Forcing UI to load.");
        return true;
      });
    }, 2500);

    return () => {
      unsubscribe();
      clearTimeout(safetyTimer);
    };
  }, [user]);

  useEffect(() => {
    if (user && userProfile?.notificationsEnabled) {
      registerPush(user);
    }
  }, [user, userProfile?.notificationsEnabled]);

  useEffect(() => {
    if (user && userProfile && userProfile.isApproved) {
      registerCurrentDevice().then(res => {
        if (res && res.code === 'DEVICE_LIMIT_EXCEEDED') {
          setDeviceLimitExceededInfo(res);
        }
      }).catch(err => {
        console.warn('[Device Registration Error]', err);
      });
    }
  }, [user, userProfile]);

  // 1. Fetch full macro state (including AI reports) when switching to macro tab
  useEffect(() => {
    if (activeTab === 'macro') {
      fetchFullMacro();
    }
  }, [activeTab, fetchFullMacro]);

  // 2. Auto-generate Market Quick Scan (Macro Summary) disabled per user request
  // Users must manually click the generate button if no report exists
  /*
  useEffect(() => {
    if (activeTab === 'macro' && !isMacroLoading && !isAnalysisRunning && indicators) {
      if (!macro.aiSummary) {
        if (quickScanAttemptRef.current.indicators === indicators &&
          quickScanAttemptRef.current.aiSummary === macro.aiSummary &&
          macro.aiSummary !== null) {
          return;
        }
        quickScanAttemptRef.current = { indicators, aiSummary: macro.aiSummary };
        generateQuickSummaryRef.current();
      }
    }
  }, [activeTab, indicators, macro.aiSummary, isAnalysisRunning, isMacroLoading]);
  */

  // 3. Stop ongoing analysis when switching tabs
  useEffect(() => {
    if (isAnalysisRunning && activeTab !== 'portfolio' && activeTab !== 'macro') {
      stopAiAnalysis();
    }
  }, [activeTab, isAnalysisRunning, stopAiAnalysis]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    const isHanging = !isAuthReady || (user && !isInitialLoadDone);

    if (isHanging) {
      timer = setTimeout(() => setShowLoadingTimeout(true), 8000);
    } else {
      setShowLoadingTimeout(false);
    }
    return () => clearTimeout(timer);
  }, [isAuthReady, isInitialLoadDone, user]);

  const isLoadingTerminal = !isAuthReady || (user && !isInitialLoadDone && !isDbError);

  if (isLoadingTerminal) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white/40">
      <div className="flex flex-col items-center gap-6">
        <div className="w-10 h-10 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
        <div className="text-center space-y-2">
          <p className="font-medium">
            {isDbError ? (userProfile?.uiLanguage === 'ko' ? '데이터 연결 오류' : 'Database Connection Error') : (userProfile?.uiLanguage === 'ko' ? '보안 연결 중...' : 'Establishing Secure Connection...')}
          </p>
          <p className="text-xs opacity-50">
            {isDbError ? (userProfile?.uiLanguage === 'ko' ? '서버와 통신할 수 없습니다.' : 'Cannot communicate with the terminal server.') : (userProfile?.uiLanguage === 'ko' ? '데이터를 불러오는 중입니다.' : 'Synchronizing terminal state...')}
          </p>
        </div>

        {(showLoadingTimeout || isDbError) && (
          <div className="mt-4 flex flex-col items-center gap-3 animate-in fade-in duration-700">
            <p className="text-xs text-amber-500/60 max-w-xs text-center">
              {isDbError
                ? (userProfile?.uiLanguage === 'ko' ? '백엔드 서비스가 응답하지 않습니다. Cloudflare Access 설정을 확인해주세요.' : 'The backend service is not responding. Please check your Cloudflare Access settings.')
                : (userProfile?.uiLanguage === 'ko' ? '연결이 지연되고 있습니다. 세션이 만료되었거나 네트워크 차단 가능성이 있습니다.' : 'Connection is taking longer than expected. Possible session timeout or network block.')}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-xs font-bold text-white transition-all border border-white/10"
              >
                {userProfile?.uiLanguage === 'ko' ? '새로고침' : 'Force Refresh'}
              </button>
              <button
                onClick={() => auth.signOut()}
                className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 rounded-lg text-xs font-bold text-red-400 transition-all border border-red-500/20"
              >
                {userProfile?.uiLanguage === 'ko' ? '로그아웃' : 'Sign Out'}
              </button>
            </div>

            {/* Diagnostic Details (Debug only) */}
            <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10 text-[10px] font-mono text-left space-y-1 opacity-80 transition-opacity max-w-[280px] overflow-auto">
              <div className="text-white/20 mb-1 uppercase text-[8px] font-bold tracking-widest flex justify-between items-center">
                <span>Diagnostic Terminal</span>
                {isDbError && <span className="text-red-500 animate-pulse">ERROR ACTIVE</span>}
              </div>
              <div className="flex justify-between"><span>AUTH:</span> <span className={isAuthReady ? 'text-emerald-500' : 'text-amber-500'}>{isAuthReady ? 'READY' : 'PENDING'}</span></div>
              <div className="flex justify-between"><span>USER:</span> <span className={user ? 'text-emerald-500' : 'text-white/40'}>{user ? 'AUTHED' : 'GUEST'}</span></div>
              <div className="flex justify-between"><span>UID:</span> <span className="text-white/40">{user?.uid ? user.uid.slice(0, 8) + '...' : 'NONE'}</span></div>
              <div className="flex justify-between"><span>LOAD:</span> <span className={isInitialLoadDone ? 'text-emerald-500' : 'text-amber-500'}>{isInitialLoadDone ? 'DONE' : 'BUSY'}</span></div>
              <div className="flex justify-between"><span>PROFILE:</span> <span className={userProfile ? 'text-emerald-500' : 'text-white/40'}>{userProfile ? 'SYNCED' : 'NULL'}</span></div>
              <div className="flex justify-between"><span>DB_ERR:</span> <span className={isDbError ? 'text-red-500' : 'text-emerald-500/40'}>{isDbError ? 'FAIL' : 'OK'}</span></div>
              <div className="flex justify-between border-t border-white/5 mt-1 pt-1"><span>SES:</span> <span className={sesStatus === 'ACTIVE' ? 'text-amber-500' : 'text-white/20'}>{sesStatus}</span></div>
              <div className="flex justify-between"><span>SW:</span> <span className={swStatus === 'ACTIVE' ? 'text-emerald-500' : 'text-white/20'}>{swStatus}</span></div>
              <div id="last-error-log" className="text-[8px] text-red-500/60 mt-1 truncate max-w-full"></div>
            </div>
            <script dangerouslySetInnerHTML={{
              __html: `
              window.onerror = function(msg, url, line) {
                const log = document.getElementById('last-error-log');
                if (log) log.textContent = 'Err: ' + msg.slice(0, 40);
              };
            `}} />

            {!isAuthReady && showLoadingTimeout && (
              <div className="flex flex-col gap-2 mt-4">
                <button
                  onClick={() => {
                    console.warn("Manual Auth Bypass triggered");
                    setIsAuthReady(true);
                    setUser(null);
                  }}
                  className="text-[10px] text-white/40 hover:text-white/80 underline uppercase tracking-widest font-bold transition-colors"
                >
                  Skip Secure Handshake
                </button>
                <button
                  onClick={async () => {
                    localStorage.clear();
                    if ('serviceWorker' in navigator) {
                      const regs = await navigator.serviceWorker.getRegistrations();
                      for (const reg of regs) await reg.unregister();
                    }
                    window.location.reload();
                  }}
                  className="text-[10px] text-red-500/40 hover:text-red-500/80 underline uppercase tracking-widest font-bold transition-colors"
                >
                  Nuclear Reset (Clear All)
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>;
  }

  if (isAdminPath) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-white font-mono">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">404 NOT FOUND</h1>
          <p className="text-zinc-500 mb-8">The requested admin route is no longer available on this domain.</p>
          <button 
            onClick={() => { window.history.pushState({}, '', '/'); setIsAdminPath(false); }}
            className="px-6 py-2 bg-emerald-500/20 text-emerald-400 rounded-xl hover:bg-emerald-500/30 transition-colors border border-emerald-500/30"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="relative min-h-screen bg-[#050505]">
        <LandingView onOpenAuth={(mode) => setAuthModalMode(mode)} />
        {authModalMode && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setAuthModalMode(null);
              }
            }}
          >
            <div className="relative w-full max-w-md">
              <Suspense fallback={<ComponentLoadingFallback />}>
                <AuthView
                  initialMode={authModalMode}
                  onClose={() => setAuthModalMode(null)}
                  onLoginSuccess={(u) => {
                    console.log("[App] Login successful, updating state...");
                    setAuthModalMode(null);
                    StorageManager.init(u.uid);
                    setUser(u);
                    setIsAuthReady(true);
                  }}
                />
              </Suspense>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Approval Guard: Only allow users with isApproved = true to proceed
  const effectiveUserId = user?.uid || (user as any)?.id;

  if (user && effectiveUserId && effectiveUserId !== 'guest') {
    // If userProfile is still null, wait but with a terminal loading fallback
    if (!userProfile) {
      // If we've been waiting too long, something is wrong
      if (isInitialLoadDone) {
        console.warn("[App] Initial load done but userProfile is still null. Displaying profile sync failure screen.");
        // Do not bypass guard - keep them on a safe loading screen or redirect
        return <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-6 p-10 text-center">
          <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center border border-red-500/30 mb-2">
            <AlertTriangle className="text-red-500 w-6 h-6" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-white">Profile Sync Failure</h2>
            <p className="text-white/40 text-sm max-w-xs mx-auto">We couldn't synchronize your profile data. This might be a temporary connection issue.</p>
          </div>
          <button
            onClick={async () => {
              try {
                await refreshProfile();
              } catch {
                window.location.reload();
              }
            }}
            className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-white font-bold transition-all border border-white/10"
          >
            Retry Sync
          </button>
          <button
            onClick={() => auth.signOut()}
            className="text-xs text-white/20 hover:text-white/40 underline uppercase tracking-widest font-bold"
          >
            Sign Out
          </button>
        </div>;
      } else {
        return (
          <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-6">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 border-4 border-emerald-500/10 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-t-emerald-500 rounded-full animate-spin"></div>
            </div>
            <div className="text-white/40 font-black uppercase tracking-[0.3em] text-[10px] animate-pulse">
              Synchronizing Profile...
            </div>
          </div>
        );
      }
    }

    if (userProfile && userProfile.isApproved !== true) {
      return (
        <Suspense fallback={<ComponentLoadingFallback />}>
          <PendingApprovalView user={user} userProfile={userProfile} onLogout={() => auth.signOut()} onCheckStatus={refreshProfile} />
        </Suspense>
      );
    }
  }


  return (
    <>
      <SecurityProtection userProfile={userProfile} />
      <Suspense fallback={<ComponentLoadingFallback />}>
      {isMobile ? (
        <MobileApp
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          user={user}
          apiKeys={apiKeys}
          saveApiKeys={saveApiKeys}
          holdings={currentPortfolioHoldings}
          macro={macro}
          indicators={indicators}
          signals={currentPortfolioSignals}
          aiAdvice={aiAdvice}
          isLoading={isLoading}
          isIntelligenceLoading={isIntelligenceLoading}
          lastUpdated={lastUpdated}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          totalValue={currentPortfolioValue}
          dailyReturnAmt={currentPortfolioDailyAmt}
          dailyReturnPct={currentPortfolioDailyPct}
          totalReturnAmt={currentPortfolioTotalAmt}
          totalReturnPct={currentPortfolioTotalPct}
          proposedTrades={proposedTrades}
          aiProposedTrades={simulatedAiTrades}
          userProfile={userProfile ? { ...userProfile, targetGoalAmount: targetGoal } : null}
          setUserProfile={setUserProfile}
          addHolding={addHolding}
          updateHolding={updateHolding}
          deleteHolding={deleteHolding}
          updateTargetWeight={updateTargetWeight}
          fetchMarketData={fetchMarketData}
          fetchFullMacro={fetchFullMacro}
          generateAiStrategy={generateAiStrategy}
          generateQuickSummary={generateQuickSummary}
          isAnalysisRunning={isAnalysisRunning}
          isDeepAnalysisRunning={isDeepAnalysisRunning}
          analysisProgress={analysisProgress}
          analysisStage={analysisStage}
          fetchDeepIntelligence={fetchDeepIntelligence}
          fetchIntelligenceOnly={fetchIntelligenceOnly}
          stopAiAnalysis={stopAiAnalysis}
          macroReportFormat={macroReportFormat}
          setMacroReportFormat={setMacroReportFormat}
          isMacroLoading={isMacroLoading}
          onSelectStock={(symbol, type) => {
            setAnalysisType(type || 'quantitative');
            setSelectedStock(symbol);
          }}
          portfolioMarketFilter={portfolioMarketFilter}
          setPortfolioMarketFilter={handlePortfolioMarketFilterChange}
          changeMarketMode={changeMarketMode}
          currencyPreference={displayCurrencyPreference}
          onCurrencyPreferenceChange={setCurrencyPreference}
          rawHoldings={rawHoldings}
          portfolioStats={portfolioStats}
          rawSignals={signals}
          optimizationData={optimizationData}
          isOptimizing={isOptimizing}
          optimizationProgress={optimizationProgress}
          optimizationStage={optimizationStage}
          runOptimization={runOptimization}
          updateMultipleTargetWeights={updateMultipleTargetWeights}
          watchlist={watchlist}
          addWatchlist={addWatchlist}
          removeWatchlist={removeWatchlist}
          onPromoteToHolding={(symbol, shares, avgCost) => {
            setPrefillSymbol(symbol);
            setPrefillShares(shares);
            setPrefillAvgCost(avgCost);
            setIsModalOpen(true);
          }}
          portfolioTab={portfolioTab}
          setPortfolioTab={setPortfolioTab}
          theme={theme}
          toggleTheme={toggleTheme}
          accounts={userAccounts}
          selectedAccountId={selectedAccountId}
          onSelectAccountId={setSelectedAccountId}
          isLiveMode={isLiveMode}
          onNavigateToSimulator={(trades) => handleStartAiSimulation(trades)}
          onSaveTradeExecution={saveTradeExecution}
          onUpgradeRequest={(requiredTier: any, featureName: any) => {
            handleUpgradeOrAddonRequest(requiredTier, featureName);
          }}
          onRefreshProfile={refreshProfile}
        />
      ) : (
        <div className="min-h-screen bg-[#050505] text-white flex overflow-hidden">
          {/* Sidebar */}
          {(() => {
            const isEffectiveCollapsed = isSidebarCollapsed && !isSidebarOpen;
            return (
              <aside className={`fixed inset-y-0 left-0 z-50 bg-[#0a0a0a] border-r border-white/5 transition-all duration-300 lg:relative lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${isEffectiveCollapsed ? 'lg:w-20 w-72' : 'w-72'}`}>
                <div className={`h-full flex flex-col ${isEffectiveCollapsed ? 'lg:p-3 p-6' : 'p-6'}`}>
                  {!isEffectiveCollapsed ? (
                    <div className="flex items-center justify-between mb-10">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-black/40 border border-cyan-500/30 p-1 flex items-center justify-center shadow-lg shadow-cyan-500/20 shrink-0">
                          <img src="/logo-emblem.png" alt="EZOptix" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex flex-col justify-center overflow-hidden">
                          <h1 className="font-extrabold text-lg tracking-tight bg-gradient-to-r from-blue-400 via-cyan-300 to-white bg-clip-text text-transparent leading-snug">EZOptix</h1>
                          <p className="text-[10px] font-medium tracking-tight text-cyan-400/80 truncate">Macro & Quant Intelligence</p>
                        </div>
                      </div>
                      <button
                        onClick={toggleSidebarCollapsed}
                        className="hidden lg:flex p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-colors cursor-pointer"
                        title={userProfile?.uiLanguage === 'ko' ? '메뉴 접기' : 'Collapse Sidebar'}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3 mb-8">
                      <div className="w-10 h-10 rounded-xl bg-black/40 border border-cyan-500/30 p-1 flex items-center justify-center shadow-lg shadow-cyan-500/20 shrink-0">
                        <img src="/logo-emblem.png" alt="EZOptix" className="w-full h-full object-contain" />
                      </div>
                      <button
                        onClick={toggleSidebarCollapsed}
                        className="hidden lg:flex p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-colors cursor-pointer"
                        title={userProfile?.uiLanguage === 'ko' ? '메뉴 펼치기' : 'Expand Sidebar'}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  <nav className="flex-1 space-y-2">
                    <NavItem
                      icon={<Activity className="w-5 h-5" />}
                      label={userProfile?.uiLanguage === 'ko' ? '대시보드' : 'Dashboard'}
                      active={activeTab === 'dashboard'}
                      collapsed={isEffectiveCollapsed}
                      onClick={() => { setActiveTab('dashboard'); setIsSidebarOpen(false); }}
                    />
                    <NavItem
                      icon={<Newspaper className="w-5 h-5" />}
                      label={userProfile?.uiLanguage === 'ko' ? '증시 속보' : 'Market News'}
                      active={activeTab === 'news'}
                      collapsed={isEffectiveCollapsed}
                      onClick={() => { setActiveTab('news'); setIsSidebarOpen(false); }}
                    />
                    <NavItem
                      icon={<CalendarIcon className="w-5 h-5" />}
                      label={userProfile?.uiLanguage === 'ko' ? '포지션 캘린더' : 'Position Calendar'}
                      active={activeTab === 'calendar'}
                      collapsed={isEffectiveCollapsed}
                      locked={!!TIER_TAB_ACCESS['calendar'] && !hasAccess(TIER_TAB_ACCESS['calendar'])}
                      onLockClick={() => setUpgradeModalConfig({ requiredTier: TIER_TAB_ACCESS['calendar'], featureName: userProfile?.uiLanguage === 'ko' ? '포지션 캘린더' : 'Position Calendar' })}
                      onClick={() => { setActiveTab('calendar'); setIsSidebarOpen(false); }}
                    />
                    <NavItem
                      icon={<TrendingUp className="w-5 h-5" />}
                      label={userProfile?.uiLanguage === 'ko' ? '매크로 분석' : 'Macro Analysis'}
                      active={activeTab === 'macro'}
                      collapsed={isEffectiveCollapsed}
                      onClick={() => { setActiveTab('macro'); setIsSidebarOpen(false); }}
                    />
                    <NavItem
                      icon={<Layers className="w-5 h-5" />}
                      label={userProfile?.uiLanguage === 'ko' ? '주요 테마 분석' : 'Major Themes'}
                      active={activeTab === 'themes'}
                      collapsed={isEffectiveCollapsed}
                      onClick={() => { setActiveTab('themes'); setIsSidebarOpen(false); }}
                    />
                    <NavItem
                      icon={<WalletIcon className="w-5 h-5" />}
                      label={userProfile?.uiLanguage === 'ko' ? '포트폴리오' : 'Portfolio'}
                      active={activeTab === 'portfolio'}
                      collapsed={isEffectiveCollapsed}
                      onClick={() => { setActiveTab('portfolio'); setIsSidebarOpen(false); }}
                    />
                    <NavItem
                      icon={<Activity className="w-5 h-5" />}
                      label={getFeatureName('OPTIONS_ANALYTICS')}
                      active={activeTab === 'options'}
                      collapsed={isEffectiveCollapsed}
                      locked={!userProfile?.activeAddons?.some(a => a.id === 'OPTIONS_ANALYTICS_ADDON')}
                      onLockClick={() => setAddonPaymentModalConfig({ addonId: 'OPTIONS_ANALYTICS_ADDON' })}
                      onClick={() => { setActiveTab('options'); setIsSidebarOpen(false); }}
                    />
                    <NavItem
                      icon={<Zap className="w-5 h-5 text-emerald-400" />}
                      label={getFeatureName('AUTOTRADING')}
                      active={activeTab === 'autotrading'}
                      collapsed={isEffectiveCollapsed}
                      onClick={() => { setActiveTab('autotrading'); setIsSidebarOpen(false); }}
                    />

                    <NavItem
                      icon={<Target className="w-5 h-5 text-blue-400" />}
                      label={userProfile?.uiLanguage === 'ko' ? '전략 시뮬레이터' : 'Strategic Simulator'}
                      active={activeTab === 'simulator' || activeTab === 'strategy'}
                      collapsed={isEffectiveCollapsed}
                      locked={!!TIER_TAB_ACCESS['simulator'] && !hasAccess(TIER_TAB_ACCESS['simulator'])}
                      onLockClick={() => setUpgradeModalConfig({ requiredTier: TIER_TAB_ACCESS['simulator'], featureName: userProfile?.uiLanguage === 'ko' ? '전략 시뮬레이터' : 'Strategic Simulator' })}
                      onClick={() => { setActiveTab('strategy'); setIsSidebarOpen(false); }}
                    />
                    <NavItem
                      icon={<Clock className="w-5 h-5 text-slate-400" />}
                      label={userProfile?.uiLanguage === 'ko' ? '성과 & 기록' : 'Performance & Logs'}
                      active={activeTab === 'history'}
                      collapsed={isEffectiveCollapsed}
                      onClick={() => { setActiveTab('history'); setIsSidebarOpen(false); }}
                    />
                    <NavItem
                      icon={<HelpCircle className="w-5 h-5" />}
                      label={userProfile?.uiLanguage === 'ko' ? '가이드' : 'Guide'}
                      active={activeTab === 'guide'}
                      collapsed={isEffectiveCollapsed}
                      onClick={() => { setActiveTab('guide'); setIsSidebarOpen(false); }}
                    />
                  </nav>

                  <div className="mt-auto space-y-2 pt-6 border-t border-white/5">
                    {!isEffectiveCollapsed ? (
                      <div className="flex items-center gap-3 px-4 py-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-xs font-bold shrink-0">
                          {(userProfile?.displayName || user?.displayName)?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div className="overflow-hidden">
                          <p className="text-xs font-medium truncate">{userProfile?.displayName || user?.displayName || user?.email}</p>
                          {/* 역할(role) 대신 실제 멤버십 등급 뱃지 표시 (레거시 FREE/PRO/PREMIUM 정규화 포함) */}
                          <div className="mt-1">
                            <TierBadge
                              size="sm"
                              tier={((userProfile?.membershipTier === 'FREE' ? 'TIER1'
                                : userProfile?.membershipTier === 'PRO' ? 'TIER3'
                                : userProfile?.membershipTier === 'PREMIUM' ? 'TIER5'
                                : userProfile?.membershipTier) || 'TIER1') as MembershipTier}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="relative group flex justify-center mb-2">
                        <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-xs font-bold cursor-pointer hover:ring-2 ring-emerald-500/50 transition-all">
                          {(userProfile?.displayName || user?.displayName)?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div className="absolute left-full ml-3 px-3 py-2 bg-zinc-900/95 backdrop-blur-md border border-white/15 text-white text-xs font-medium rounded-xl shadow-2xl whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50">
                          <p className="font-semibold">{userProfile?.displayName || user?.displayName || user?.email}</p>
                          <div className="mt-1">
                            <TierBadge
                              size="sm"
                              tier={((userProfile?.membershipTier === 'FREE' ? 'TIER1'
                                : userProfile?.membershipTier === 'PRO' ? 'TIER3'
                                : userProfile?.membershipTier === 'PREMIUM' ? 'TIER5'
                                : userProfile?.membershipTier) || 'TIER1') as MembershipTier}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => setActiveTab('settings')}
                      className={`w-full flex items-center justify-center gap-2 py-2 mb-2 rounded-xl border transition-all text-xs font-bold relative group ${
                        isEffectiveCollapsed ? 'h-10 px-0' : 'px-4'
                      } ${activeTab === 'settings'
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                        : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white'
                        }`}
                    >
                      <Settings className="w-4 h-4 shrink-0" />
                      {!isEffectiveCollapsed && (userProfile?.uiLanguage === 'ko' ? '설정' : 'Settings')}
                      {isEffectiveCollapsed && (
                        <div className="absolute left-full ml-3 px-3 py-1.5 bg-zinc-900/95 backdrop-blur-md border border-white/15 text-white text-xs font-medium rounded-xl shadow-2xl whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50">
                          {userProfile?.uiLanguage === 'ko' ? '설정' : 'Settings'}
                        </div>
                      )}
                    </button>
                    <button
                      onClick={() => auth.signOut()}
                      className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-white/5 hover:bg-red-500/10 hover:text-red-400 text-white/60 text-xs font-bold transition-all relative group ${
                        isEffectiveCollapsed ? 'h-10 px-0' : 'px-4'
                      }`}
                    >
                      <LogOut className="w-4 h-4 shrink-0" />
                      {!isEffectiveCollapsed && (userProfile?.uiLanguage === 'ko' ? '로그아웃' : 'Logout')}
                      {isEffectiveCollapsed && (
                        <div className="absolute left-full ml-3 px-3 py-1.5 bg-zinc-900/95 backdrop-blur-md border border-white/15 text-white text-xs font-medium rounded-xl shadow-2xl whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50">
                          {userProfile?.uiLanguage === 'ko' ? '로그아웃' : 'Logout'}
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </aside>
            );
          })()}

          <main className="flex-1 flex flex-col h-screen overflow-y-auto custom-scrollbar">
            <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5 px-8 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-2 hover:bg-white/5 rounded-lg">
                  <Menu className="w-6 h-6" />
                </button>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2 mt-0.5">
                    {macro?.marketRegime && (
                      <RegimeBadge regime={macro.marketRegime} lang={userProfile?.uiLanguage} />
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-6">
                {/* Market Mode Switcher */}
                <div className="flex bg-black/50 border border-white/10 rounded-xl p-1 shadow-inner">
                  <button
                    onClick={() => changeMarketMode('US')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${userProfile?.targetMarket === 'US' ? 'bg-gradient-to-r from-blue-600/80 to-blue-500/80 text-white shadow-lg shadow-blue-500/20' : 'text-white/40 hover:text-white/80'}`}
                  >
                    🇺🇸 {userProfile?.uiLanguage === 'ko' ? '미국' : 'US'}
                  </button>
                  <button
                    onClick={() => changeMarketMode('KR')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${userProfile?.targetMarket === 'KR' ? 'bg-gradient-to-r from-emerald-600/80 to-teal-500/80 text-white shadow-lg shadow-emerald-500/20' : 'text-white/40 hover:text-white/80'}`}
                  >
                    🇰🇷 {userProfile?.uiLanguage === 'ko' ? '한국' : 'KR'}
                  </button>
                </div>

                <div className="hidden xl:flex items-center gap-2 text-sm text-white/40 font-mono">
                  <CalendarIcon className="w-4 h-4" />
                  <span>{new Date().toLocaleDateString()}</span>
                </div>

                <div className="flex items-center gap-2 border-l border-white/10 pl-4">
                  <button
                    onClick={() => {
                      fetchMarketData(true);
                      window.dispatchEvent(new CustomEvent('app:refresh-notifications'));
                    }}
                    className="p-2 hover:bg-white/5 rounded-xl transition-all text-white/40 hover:text-white"
                    disabled={isLoading}
                  >
                    <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                  </button>
                  <NotificationCenter />

                  <button
                    onClick={toggleTheme}
                    className="p-2 hover:bg-white/5 rounded-xl transition-all text-white/40 hover:text-white ml-2 flex items-center justify-center"
                    title={userProfile?.uiLanguage === 'ko' ? '화면 테마 변경' : 'Toggle Theme'}
                  >
                    {theme === 'dark' ? (
                      <Sun className="w-5 h-5 text-amber-400 hover:text-amber-300 transition-colors animate-[spin_10s_linear_infinite]" />
                    ) : (
                      <Moon className="w-5 h-5 text-indigo-400 hover:text-indigo-300 transition-colors" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('mypage')}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 border border-white/10 overflow-hidden hover:ring-2 ring-emerald-500/50 transition-all ml-2"
                  >
                    {user?.photoURL ? (
                      <img src={user.photoURL} alt={userProfile?.displayName || user?.displayName || 'User'} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs font-bold text-black">
                        {(userProfile?.displayName || user?.displayName)?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </header>

            {/* Scrollable Content */}
            <div className={`flex-1 flex flex-col min-h-0 ${activeTab === 'news' ? 'overflow-hidden' : 'overflow-y-auto custom-scrollbar'}`}>
              <div className={`max-w-[1600px] w-full mx-auto ${activeTab === 'news' ? 'p-4 md:p-6 flex-1 flex flex-col min-h-0' : 'p-8 space-y-8'}`}>
                {/*
                  탭 뷰 전용 Suspense 경계.
                  lazy 로드된 탭 뷰가 최초 suspend될 때 바깥 Suspense(사이드바/헤더 포함)까지
                  언마운트되어 "전체 페이지 리로드"처럼 보이던 현상을 방지한다.
                  이 경계 안쪽만 로딩 스피너로 대체되므로 메뉴바는 항상 고정된다.
                */}
                <Suspense fallback={<ComponentLoadingFallback />}>
                {activeTab === 'dashboard' && (
                  <div className="space-y-8">
                    <MacroShockBanner lang={userProfile?.uiLanguage === 'en' ? 'en' : 'ko'} />
                    {/* Dashboard Header with Account Selector */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white/5 border border-white/10 rounded-[1.5rem] p-6 backdrop-blur-md gap-4">
                      <div className="flex flex-col gap-1">
                        <h2 className="text-xl font-black tracking-tight text-white flex items-center gap-2">
                          <LayoutDashboard className="w-5 h-5 text-emerald-400" />
                          {userProfile?.uiLanguage === 'ko' ? '종합 대시보드' : 'Integrated Dashboard'}
                        </h2>
                        <p className="text-xs text-white/40">
                          {userProfile?.uiLanguage === 'ko'
                            ? '매크로 인디케이터와 선택된 계좌의 보유 자산을 한눈에 분석합니다.'
                            : 'Analyze macro indicators and selected account holdings at a glance.'}
                        </p>
                      </div>
                      {!isLiveMode ? (
                        <div className="flex items-center gap-2">
                          <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs px-3.5 py-2 rounded-xl font-bold uppercase tracking-wider flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                            {userProfile?.uiLanguage === 'ko' ? '모의투자 Sandbox' : 'Paper Sandbox'}
                          </div>
                          {userAccounts.length > 1 && (
                            <AccountSelector
                              accounts={userAccounts}
                              selectedAccountId={selectedAccountId}
                              onSelectAccountId={setSelectedAccountId}
                              language={userProfile?.uiLanguage}
                            />
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs px-3.5 py-2 rounded-xl font-bold uppercase tracking-wider flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-400" />
                            {userProfile?.uiLanguage === 'ko' ? '실거래 계좌' : 'Live Trading'}
                          </div>
                          <AccountSelector
                            accounts={userAccounts}
                            selectedAccountId={selectedAccountId}
                            onSelectAccountId={setSelectedAccountId}
                            language={userProfile?.uiLanguage}
                          />
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <IndicesMarquee indicators={indicators} marketMode={userProfile?.targetMarket} />
                      <FuturesMarquee indicators={indicators} marketMode={userProfile?.targetMarket} />
                    </div>

                    <MacroTopStats
                      macro={macro}
                      indicators={indicators}
                      language={userProfile?.uiLanguage}
                      marketMode={userProfile?.targetMarket}
                      isLoading={isLoading}
                      onForceRefresh={() => fetchFullMacro(true)}
                    />

                    <CommoditiesMarquee indicators={indicators} marketMode={userProfile?.targetMarket} />

                    <SectorHeatmap indicators={indicators} marketMode={userProfile?.targetMarket} />

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start">
                      <div className="xl:col-span-2 space-y-8">
                        <PortfolioOverviewWidget
                          holdings={holdings}
                          signals={signals}
                          onNavigateToPortfolio={() => setActiveTab('portfolio')}
                          language={userProfile?.uiLanguage}
                          totalValue={totalValue}
                          targetGoalAmount={targetGoal}
                          onNavigateToSettings={() => setActiveTab('settings')}
                          marketMode={userProfile?.targetMarket}
                          currencyPreference={displayCurrencyPreference}
                          onCurrencyPreferenceChange={setCurrencyPreference}
                          onSelectTechnicalStock={setSelectedTechnicalSymbol}
                        />

                        {/* 실시간 뉴스 트렌드 포착 종목 위젯 (실시간 포트폴리오 섹션 하단 최적 균형 배치) */}
                        <TrendRecommendationsWidget
                          language={userProfile?.uiLanguage}
                          marketMode={userProfile?.targetMarket}
                          watchlistSymbols={watchlist.map(w => w.symbol.toUpperCase())}
                          onToggleWatchlist={handleToggleTrendWatchlist}
                        />
                      </div>
                      <div className="xl:col-span-1">
                        <MacroRegimesCard
                          macro={macro}
                          indicators={indicators}
                          language={userProfile?.uiLanguage}
                          marketMode={userProfile?.targetMarket}
                          mode="items"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'portfolio' && (
                  <div className="space-y-8">
                    {/* Integrated Portfolio Glassmorphism Switcher */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white/5 border border-white/10 rounded-[1.5rem] p-6 backdrop-blur-md gap-4">
                      <div className="flex flex-col gap-1">
                        <h2 className="text-xl font-black tracking-tight text-white flex items-center gap-2">
                          <WalletIcon className="w-5 h-5 text-purple-400" />
                          {userProfile?.uiLanguage === 'ko'
                            ? (isLiveMode ? '실거래 포트폴리오 자산 관리' : '모의투자 포트폴리오 자산 관리')
                            : (isLiveMode ? 'Live Portfolio Management' : 'Paper Portfolio Management')}
                        </h2>
                        <p className="text-xs text-white/40">
                          {userProfile?.uiLanguage === 'ko'
                            ? (isLiveMode
                                ? '실거래 연동 증권사 계좌의 보유 자산 확인 및 통합 가치를 조회할 수 있습니다.'
                                : '모의투자 전용 샌드박스 종목 및 모의 포트폴리오를 안전하게 시뮬레이션합니다.')
                            : (isLiveMode
                                ? 'View and manage real holdings across linked brokerage accounts.'
                                : 'Simulate and manage holdings in a safe paper trading environment.')}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-4">
                        {!isLiveMode ? (
                          <div className="flex items-center gap-2">
                            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs px-3.5 py-2 rounded-xl font-bold uppercase tracking-wider flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                              {userProfile?.uiLanguage === 'ko' ? '모의투자 Sandbox' : 'Paper Sandbox'}
                            </div>
                            {userAccounts.length > 1 && (
                              <AccountSelector
                                accounts={userAccounts}
                                selectedAccountId={selectedAccountId}
                                onSelectAccountId={setSelectedAccountId}
                                language={userProfile?.uiLanguage}
                              />
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs px-3.5 py-2 rounded-xl font-bold uppercase tracking-wider flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-emerald-400" />
                              {userProfile?.uiLanguage === 'ko' ? '실거래 계좌' : 'Live Trading'}
                            </div>
                            <AccountSelector
                              accounts={userAccounts}
                              selectedAccountId={selectedAccountId}
                              onSelectAccountId={setSelectedAccountId}
                              language={userProfile?.uiLanguage}
                            />
                          </div>
                        )}
                        <div className="flex bg-black/40 border border-white/5 p-1 rounded-xl shadow-inner">
                          {([
                            { key: 'ALL', label: userProfile?.uiLanguage === 'ko' ? '통합 🌐' : 'Unified 🌐' },
                            { key: 'US', label: userProfile?.uiLanguage === 'ko' ? '미국 🇺🇸' : 'US 🇺🇸' },
                            { key: 'KR', label: userProfile?.uiLanguage === 'ko' ? '한국 🇰🇷' : 'KR 🇰🇷' }
                          ] as const).map((opt) => (
                            <button
                              key={opt.key}
                              onClick={() => handlePortfolioMarketFilterChange(opt.key)}
                              className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${portfolioMarketFilter === opt.key
                                ? 'bg-gradient-to-r from-purple-600/80 to-indigo-500/80 text-white shadow-lg shadow-purple-500/20'
                                : 'text-white/40 hover:text-white/80'
                                }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <MacroShockBanner lang={userProfile?.uiLanguage === 'en' ? 'en' : 'ko'} />

                    <PortfolioSummary
                      totalValue={currentPortfolioValue}
                      dailyReturnAmt={currentPortfolioDailyAmt}
                      dailyReturnPct={currentPortfolioDailyPct}
                      totalReturnAmt={currentPortfolioTotalAmt}
                      totalReturnPct={currentPortfolioTotalPct}
                      lastUpdated={lastUpdated}
                      targetGoalAmount={targetGoal}
                      language={userProfile?.uiLanguage}
                      marketMode={portfolioMarketFilter}
                      currencyPreference={displayCurrencyPreference}
                      onCurrencyPreferenceChange={setCurrencyPreference}
                      holdings={currentPortfolioHoldings}
                    />

                    {/* Portfolio Time-Series Valuation & Macro Risk Chart (3개 탭 공통 상단 고정 유지) */}
                    <PortfolioTimeSeriesChart
                      language={userProfile?.uiLanguage}
                      currencyPreference={displayCurrencyPreference}
                      exchangeRate={currentExchangeRate}
                      currentTotalValue={currentPortfolioValue}
                      selectedAccountId={selectedAccountId}
                      accountName={userAccounts.find((a: any) => a.id === selectedAccountId)?.account_name}
                    />

                    {/* 영역 구분 타이틀: 포트폴리오 운용 & 전략 데스크 */}
                    <div className="flex items-center gap-3 pt-4 pb-1">
                      <div className="p-2.5 rounded-2xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/30 text-purple-400 shadow-md">
                        <Layers className="w-5 h-5 animate-pulse" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-black text-white italic tracking-tight uppercase">
                            {userProfile?.uiLanguage === 'ko' ? '포트폴리오 운용 & 전략 데스크' : 'PORTFOLIO MANAGEMENT & STRATEGY DESK'}
                          </h3>
                          <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 tracking-widest uppercase">
                            MANAGEMENT
                          </span>
                        </div>
                        <p className="text-xs text-white/40 font-medium mt-0.5">
                          {userProfile?.uiLanguage === 'ko'
                            ? '보유 자산 및 비중 관리, 시장 시그널 & 리스크 탐지, AI 퀀트 전략 최적화'
                            : 'Asset allocation management, real-time market signals, and AI strategy rebalancing'}
                        </p>
                      </div>
                    </div>

                    {/* Segmented Sub-Navigation for Progressive Disclosure */}
                    <div className="flex items-center gap-1.5 p-1.5 bg-black/40 border border-white/10 rounded-2xl w-full sm:w-fit shadow-inner">
                      <button
                        onClick={() => setPortfolioMainSection('holdings')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                          portfolioMainSection === 'holdings'
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/25'
                            : 'text-white/40 hover:text-white/80 hover:bg-white/5'
                        }`}
                      >
                        <WalletIcon className="w-3.5 h-3.5" />
                        <span>{userProfile?.uiLanguage === 'ko' ? '보유 자산 및 배분' : 'Holdings & Allocation'}</span>
                      </button>
                      <button
                        onClick={() => setPortfolioMainSection('signals')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                          portfolioMainSection === 'signals'
                            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/25'
                            : 'text-white/40 hover:text-white/80 hover:bg-white/5'
                        }`}
                      >
                        <Activity className="w-3.5 h-3.5" />
                        <span>{userProfile?.uiLanguage === 'ko' ? '시장 시그널 & 리스크' : 'Signals & Risk'}</span>
                      </button>
                      <button
                        onClick={() => setPortfolioMainSection('optimization')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                          portfolioMainSection === 'optimization'
                            ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                            : 'text-white/40 hover:text-white/80 hover:bg-white/5'
                        }`}
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>{userProfile?.uiLanguage === 'ko' ? 'AI 전략 최적화 데스크' : 'AI Optimization'}</span>
                      </button>
                    </div>

                    <div className="space-y-6">
                      {portfolioMainSection === 'holdings' && (
                        <PortfolioManager
                        totalValue={currentPortfolioValue}
                        selectedAccountId={selectedAccountId}
                        accounts={userAccounts}
                        holdings={currentPortfolioHoldings}
                        signals={signals}
                        isLiveMode={isLiveMode}
                        paperExecutionMode={paperExecutionMode}
                        activeTab={portfolioTab}
                        onTabChange={setPortfolioTab}
                        initialSimulateSymbol={activeSimulateSymbol}
                        onUpdate={updateHolding}
                        onDelete={deleteHolding}
                        onLiquidate={liquidateHolding}
                        onAdd={() => {
                          if (isLiveMode && tier !== 'TIER1') {
                            showToast(
                              userProfile?.uiLanguage === 'ko'
                                ? '실거래 모드에서는 Tier 1 사용자만 수동 자산 추가가 가능하며, Tier 2 이상의 연동 사용자는 증권사 계좌 동기화를 통해 자산이 자동 관리됩니다.'
                                : 'In live trading mode, manual asset addition is only available for Tier 1 users. Tier 2+ users sync assets automatically via brokerage accounts.',
                              'error'
                            );
                            return;
                          }
                          if (!systemFeatures.addAssetEnabled) {
                            showToast(
                              userProfile?.uiLanguage === 'ko'
                                ? '자산 추가 기능이 관리자에 의해 비활성화되었습니다.'
                                : 'Add Asset feature is disabled by administrator.',
                              'error'
                            );
                            return;
                          }
                          setPrefillSymbol('');
                          setPrefillShares(0);
                          setPrefillAvgCost(0);
                          setIsModalOpen(true);
                        }}
                        onSelectStock={(s, type) => {
                          setSelectedStock(s);
                          if (type) setAnalysisType(type as any);
                        }}
                        language={userProfile?.uiLanguage}
                        marketMode={portfolioMarketFilter}
                        currencyPreference={displayCurrencyPreference}
                        exchangeRate={currentExchangeRate}
                        onShowAllocation={() => setIsAllocationModalOpen(true)}
                        onBulkAdd={addBulkHoldings}
                        onSelectTechnicalStock={(symbol) => {
                          if (!systemFeatures.technicalAnalysisEnabled) {
                            showToast(
                              userProfile?.uiLanguage === 'ko'
                                ? '기술적 분석 보기 기능이 관리자에 의해 비활성화되었습니다.'
                                : 'Technical Analysis view is disabled by administrator.',
                              'error'
                            );
                            return;
                          }
                          setSelectedTechnicalSymbol(symbol);
                        }}
                        isImportScreenshotEnabled={systemFeatures.importScreenshotEnabled}
                        isAddAssetEnabled={systemFeatures.addAssetEnabled}
                        isTechnicalAnalysisEnabled={systemFeatures.technicalAnalysisEnabled}
                        watchlist={watchlist}
                        watchlistLimit={limits?.watchlistLimit}
                        holdingsLimit={limits?.holdingsLimit}
                        watchlistCount={watchlist.length}
                        holdingsCount={rawHoldings.filter(h => !['USD', 'KRW', 'CASH'].includes(h.symbol.toUpperCase()) && Number(h.shares) > 0).length}
                        onAddWatchlist={async (symbol, name, market, notes) => {
                          if (!canAddWatchlist(watchlist.length)) {
                            return;
                          }
                          await addWatchlist(symbol, name, market, notes);
                        }}
                        onRemoveWatchlist={removeWatchlist}
                        onPromoteToHolding={(symbol, shares, avgCost) => {
                          // 보유종목 승격 시에도 추가 한도 사전 체크
                          const currentHoldingsCount = rawHoldings.filter(h => !['USD', 'KRW', 'CASH'].includes(h.symbol.toUpperCase()) && Number(h.shares) > 0).length;
                          const isExisting = rawHoldings.some(h => h.symbol.toUpperCase() === symbol.toUpperCase() && Number(h.shares) > 0);
                          if (!isExisting && !canAddHolding(currentHoldingsCount)) {
                            return;
                          }
                          setPrefillSymbol(symbol);
                          setPrefillShares(shares);
                          setPrefillAvgCost(avgCost);
                          setIsModalOpen(true);
                        }}
                        onSaveTradeExecution={saveTradeExecution}
                        onShowToast={showToast}
                        membershipTier={tier}
                      />
                    )}

                    {portfolioMainSection === 'signals' && (
                      <div className="-mt-2">
                        <h3 className="text-base font-black text-white/95 flex items-center gap-2 tracking-tight uppercase mb-3">
                          <Activity className="w-4 h-4 text-emerald-400" />
                          {userProfile?.uiLanguage === 'ko' ? '시장 시그널 및 리스크 분석' : 'Market Signals & Risk Analysis'}
                        </h3>
                        {systemFeatures.quantSignalsEnabled ? (
                          <SignalsTable
                            signals={currentPortfolioSignals}
                            onSelectStock={(s, type) => {
                              setSelectedStock(s);
                              if (type) setAnalysisType(type as any);
                            }}
                            language={userProfile?.uiLanguage}
                            marketMode={portfolioMarketFilter}
                            watchlistSymbols={watchlist.map(w => w.symbol)}
                            onToggleWatchlist={async (symbol) => {
                              const isListed = watchlist.some(w => w.symbol.toUpperCase() === symbol.toUpperCase());
                              if (isListed) {
                                const market = symbol.endsWith('.KS') || symbol.endsWith('.KQ') ? 'KR' : 'US';
                                await removeWatchlist(symbol, market);
                                showToast(userProfile?.uiLanguage === 'ko'
                                  ? `${symbol}이 관심종목에서 삭제되었습니다.`
                                  : `${symbol} removed from watchlist.`
                                );
                              } else {
                                if (!canAddWatchlist(watchlist.length)) {
                                  return;
                                }
                                const name = signals.find(s => s.symbol.toUpperCase() === symbol.toUpperCase())?.name || '';
                                const market = symbol.endsWith('.KS') || symbol.endsWith('.KQ') ? 'KR' : 'US';
                                await addWatchlist(symbol, name, market, '');
                                showToast(userProfile?.uiLanguage === 'ko'
                                  ? `${symbol}이 관심종목에 등록되었습니다.`
                                  : `${symbol} added to watchlist.`
                                );
                              }
                            }}
                          />
                        ) : systemFeatures.featureDisabledMode === 'HIDE' ? null : (
                          <div className="glass-premium rounded-[2.5rem] border border-white/5 p-8 flex flex-col items-center justify-center text-center min-h-[300px] bg-black/20">
                            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
                              <AlertTriangle className="w-6 h-6 text-amber-500" />
                            </div>
                            <h4 className="text-white font-black uppercase italic text-sm">
                              {userProfile?.uiLanguage === 'ko' ? '퀀트 시그널 분석 일시 비활성화' : 'Quant Signals Analysis Temporarily Disabled'}
                            </h4>
                            <p className="text-xs text-white/40 max-w-sm mt-2 leading-relaxed">
                              {userProfile?.uiLanguage === 'ko'
                                ? '현재 시스템 점검으로 인해 퀀트 시그널 및 수급 분석 기능이 일시적으로 제한되었습니다. 관리자에게 문의해 주세요.'
                                : 'Quant signals and supply/demand analysis are temporarily disabled due to system maintenance. Please contact the administrator.'}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Full Width Optimization Section */}

                    {/* AI & Quant 포트폴리오 최적화 데스크 */}
                    {portfolioMainSection === 'optimization' && (
                      <div className="pt-2 space-y-6">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center border border-emerald-500/20 text-emerald-400">
                            <Sparkles className="w-6 h-6 animate-pulse" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-0.5">
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400/80">AI & Quant Desk</span>
                            </div>
                            <h3 className="text-base font-black text-white/95 tracking-tight uppercase">
                              {userProfile?.uiLanguage === 'ko' ? 'AI 및 퀀트 전략 최적화 데스크' : 'AI & Quant Strategy Desk'}
                            </h3>
                          </div>
                        </div>

                        {/* Sub-Tab Controls */}
                        <div className="flex bg-black/40 border border-white/5 p-1 rounded-xl shadow-inner">
                          <button
                            onClick={() => setOptSubTab('ai')}
                            className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all flex items-center gap-1.5 ${optSubTab === 'ai'
                              ? 'bg-gradient-to-r from-purple-600/80 to-indigo-500/80 text-white shadow-lg shadow-purple-500/20'
                              : 'text-white/40 hover:text-white/80'
                              }`}
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            {userProfile?.uiLanguage === 'ko' ? 'AI 전술적 리밸런싱' : 'AI Tactical'}
                          </button>
                          <button
                            onClick={() => setOptSubTab('quant')}
                            className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all flex items-center gap-1.5 ${optSubTab === 'quant'
                              ? 'bg-gradient-to-r from-emerald-600/80 to-teal-500/80 text-white shadow-lg shadow-emerald-500/20'
                              : 'text-white/40 hover:text-white/80'
                              }`}
                          >
                            <Target className="w-3.5 h-3.5" />
                            {userProfile?.uiLanguage === 'ko' ? '퀀트 비중 최적화 (MVO)' : 'Quant MVO'}
                          </button>
                          <button
                            onClick={() => setOptSubTab('xray')}
                            className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all flex items-center gap-1.5 ${optSubTab === 'xray'
                              ? 'bg-gradient-to-r from-purple-600/80 to-indigo-500/80 text-white shadow-lg shadow-purple-500/20'
                              : 'text-white/40 hover:text-white/80'
                              }`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            {userProfile?.uiLanguage === 'ko' ? '포트폴리오 X-Ray (숨은 리스크)' : 'Portfolio X-Ray'}
                          </button>
                        </div>
                      </div>

                      {/* Tab 1: AI 전술적 리밸런싱 */}
                      {optSubTab === 'ai' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-300">
                          {/* Un-analyzed state */}
                          {!aiAdvice && !isIntelligenceLoading && !isAnalysisRunning && (
                            <div className="flex flex-col items-center justify-center p-12 text-center rounded-[2rem] border border-white/5 bg-[#0a0a0a] gap-5">
                              <div className="w-14 h-14 rounded-2xl bg-purple-500/10 border border-purple-500/25 flex items-center justify-center">
                                <Sparkles className="w-7 h-7 text-purple-400 animate-pulse" />
                              </div>
                              <div>
                                <h4 className="text-base font-black text-white">
                                  {userProfile?.uiLanguage === 'ko' ? 'AI 포트폴리오 리밸런싱 보고서 생성' : 'Generate AI Portfolio Strategy Report'}
                                </h4>
                                <p className="text-sm text-white/40 mt-1.5 max-w-md leading-relaxed font-medium">
                                  {userProfile?.uiLanguage === 'ko'
                                    ? (isLiveMode
                                        ? '현재 매크로 체제와 포트폴리오 데이터를 바탕으로 전술적 투자 테마와 신규 자산 추천, 목표 가치 달성을 위한 가이드를 생성합니다.'
                                        : '현재 매크로 체제와 모의 포트폴리오 데이터를 바탕으로 전술적 시장 테마, 관심 후보 분석, 목표 가치 시뮬레이션 가이드를 생성합니다.')
                                    : 'Generates tactical investment thesis, portfolio simulation insights, and path-to-goal guides based on current macro regime and portfolio.'}
                                </p>
                              </div>
                              <button
                                onClick={async () => {
                                  if (macro?.aiSummary) {
                                    await generateAiStrategy();
                                  } else {
                                    await fetchDeepIntelligence(true);
                                  }
                                }}
                                className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white font-black text-xs rounded-xl shadow-lg transition-all"
                              >
                                <Sparkles className="w-4 h-4" />
                                {userProfile?.uiLanguage === 'ko' ? 'AI 리포트 생성' : 'Generate AI Report'}
                              </button>
                            </div>
                          )}

                          {/* Loading state */}
                          {(isIntelligenceLoading || isAnalysisRunning) && (
                            <div className="p-8 bg-[#0a0a0a] border border-purple-500/20 rounded-[2rem] backdrop-blur-xl max-w-xl mx-auto w-full">
                              <div className="flex items-center gap-4 mb-6">
                                <div className="relative w-10 h-10 flex-shrink-0">
                                  <div className="absolute inset-0 rounded-full border-2 border-purple-500/20 animate-ping" />
                                  <div className="absolute inset-0 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-black text-white truncate">
                                    {analysisStage === 1 && (userProfile?.uiLanguage === 'ko' ? '거시경제 국면 분석 중...' : 'Analyzing macro regime...')}
                                    {analysisStage === 2 && (userProfile?.uiLanguage === 'ko' ? '시장 테마 및 팩터 스크리닝 연산 중...' : 'Extracting factor screening data...')}
                                    {analysisStage === 3 && (userProfile?.uiLanguage === 'ko' ? '포트폴리오 변동성 시뮬레이션 보고서 생성 중...' : 'Generating portfolio simulation report...')}
                                    {analysisStage === 0 && (userProfile?.uiLanguage === 'ko' ? '분석 준비 중...' : 'Preparing...')}
                                  </p>
                                  <p className="text-[11px] text-white/30 mt-1">
                                    {userProfile?.uiLanguage === 'ko' ? '거시 지표와 포트폴리오 데이터를 분석하여 퀀트 스크리닝 지표를 계산합니다.' : 'Analyzing macro indicators and holdings to calculate quant screening metrics.'}
                                  </p>
                                </div>
                              </div>
                              {/* Percentage Progress Bar */}
                              <div className="mb-5">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-[10px] font-black uppercase tracking-widest text-white/30">
                                    {userProfile?.uiLanguage === 'ko' ? '분석 진행률' : 'Analysis Progress'}
                                  </span>
                                  <span className="text-sm font-mono font-black text-purple-400">
                                    {Math.round(analysisProgress)}%
                                  </span>
                                </div>
                                <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-purple-600 to-purple-400 rounded-full transition-all duration-500 ease-out relative"
                                    style={{ width: `${analysisProgress}%` }}
                                  >
                                    <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full" />
                                  </div>
                                </div>
                              </div>
                              {/* Stage Indicators */}
                              <div className="space-y-2">
                                {[
                                  { stage: 1, label: userProfile?.uiLanguage === 'ko' ? '거시 지표 분석' : 'Macro Indicators Scan' },
                                  { stage: 2, label: userProfile?.uiLanguage === 'ko' ? '투자 테마 & 자산 추출' : 'Tactical Asset Extraction' },
                                  { stage: 3, label: userProfile?.uiLanguage === 'ko' ? '리밸런싱 대응 보고서' : 'Strategic Advisory Generation' },
                                ].map(({ stage, label }) => (
                                  <div key={stage} className="flex items-center gap-3">
                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-black transition-all duration-500 ${analysisStage > stage ? 'bg-purple-500 text-white' :
                                      analysisStage === stage ? 'bg-purple-500/30 border border-purple-500 animate-pulse' :
                                        'bg-white/5 border border-white/10'
                                      }`}>
                                      {analysisStage > stage ? '✓' : stage}
                                    </div>
                                    <span className={`text-[11px] font-bold transition-all duration-300 ${analysisStage >= stage ? 'text-white/70' : 'text-white/20'
                                      }`}>{label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Completed state */}
                          {aiAdvice && !isIntelligenceLoading && !isAnalysisRunning && (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                              <div className="lg:col-span-2">
                                <TierGate
                                  requiredTier="TIER2"
                                  currentTier={tier}
                                  featureName={userProfile?.uiLanguage === 'ko' ? 'AI 전술적 대응 보고서' : 'AI Tactical Advice Report'}
                                  userProfile={userProfile}
                                  onUpgradeSuccess={refreshProfile}
                                >
                                  <AiAnalysisCard
                                    aiAdvice={aiAdvice}
                                    isLoading={isIntelligenceLoading}
                                    showSummary={false}
                                    title={userProfile?.uiLanguage === 'ko' ? "AI 전술적 대응 보고서" : "AI Tactical Advice Report"}
                                    language={userProfile?.uiLanguage}
                                    errorInfo={aiErrorInfo}
                                    generationDate={macro?.aiAnalysisDate}
                                    onRunAnalysis={async () => {
                                      if (macro?.aiSummary) {
                                        await generateAiStrategy();
                                      } else {
                                        await fetchDeepIntelligence(true);
                                      }
                                    }}
                                    onStopAnalysis={() => stopAiAnalysis()}
                                  />
                                </TierGate>
                                <div className="mt-6">
                                  <TrendRecommendationsWidget
                                    language={userProfile?.uiLanguage}
                                    marketMode={userProfile?.targetMarket}
                                    watchlistSymbols={watchlist.map(w => w.symbol.toUpperCase())}
                                    onToggleWatchlist={handleToggleTrendWatchlist}
                                  />
                                </div>
                              </div>
                              <div className="lg:col-span-1 space-y-6">
                                <TierGate
                                  requiredTier="TIER3"
                                  currentTier={tier}
                                  featureName={userProfile?.uiLanguage === 'ko' ? (isLiveMode ? '추천 매매 종목' : '비중 편차 시뮬레이션') : 'Proposed Rebalance'}
                                  userProfile={userProfile}
                                  onUpgradeSuccess={refreshProfile}
                                >
                                  <ProposedTradesCard
                                    trades={proposedTrades}
                                    isLoading={isIntelligenceLoading}
                                    language={userProfile?.uiLanguage}
                                    marketMode={portfolioMarketFilter}
                                    onSimulate={() => handleStartAiSimulation(proposedTrades)}
                                  />
                                </TierGate>
                                <TierGate
                                  requiredTier="TIER3"
                                  currentTier={tier}
                                  featureName={userProfile?.uiLanguage === 'ko' ? (isLiveMode ? '신규 추천 자산' : '신규 관심 후보 분석') : 'Candidate Assets'}
                                  userProfile={userProfile}
                                  onUpgradeSuccess={refreshProfile}
                                >
                                  <NewRecommendationsGrid
                                    aiAdvice={aiAdvice}
                                    isLoading={isIntelligenceLoading}
                                    language={userProfile?.uiLanguage}
                                    marketMode={portfolioMarketFilter}
                                    onSimulate={(recs) => handleStartSimulationWithRecommendations(recs)}
                                  />
                                </TierGate>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Tab 2: 퀀트 비중 최적화 */}
                      {optSubTab === 'quant' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-300">
                          {/* Un-optimized CTA */}
                          {!optimizationData && !isOptimizing && (
                            <div className="flex flex-col items-center justify-center p-12 text-center rounded-[2rem] border border-white/5 bg-[#0a0a0a] gap-5">
                              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
                                <Target className="w-7 h-7 text-emerald-400" />
                              </div>
                              <div>
                                <h4 className="text-base font-black text-white">
                                  {userProfile?.uiLanguage === 'ko' ? '퀀트 자산 비중 최적화 실행' : 'Run Quant Portfolio Optimization'}
                                </h4>
                                <p className="text-sm text-white/40 mt-1.5 max-w-md leading-relaxed font-medium">
                                  {userProfile?.uiLanguage === 'ko'
                                    ? '현대 포트폴리오 이론(MPT)에 입각하여, 주어진 위험 한도에서 기대 수익률과 샤프 지수를 극대화하는 자산 배분을 수리적으로 산출합니다.'
                                    : 'Calculates mathematically the asset allocation weights that maximize expected return and Sharpe ratio based on MPT.'}
                                </p>
                              </div>
                              <button
                                onClick={() => handleRunOptimization()}
                                disabled={!systemFeatures.portfolioOptimizationEnabled}
                                className={`flex items-center gap-2 px-6 py-3 font-black text-xs rounded-xl shadow-lg transition-all ${systemFeatures.portfolioOptimizationEnabled
                                  ? 'bg-emerald-500 hover:bg-emerald-400 text-black cursor-pointer'
                                  : 'bg-white/5 border border-white/10 text-white/20 cursor-not-allowed'
                                  }`}
                              >
                                <Target className="w-4 h-4" />
                                {userProfile?.uiLanguage === 'ko' ? '최적화 계산 실행' : 'Run Optimization'}
                              </button>
                            </div>
                          )}

                          {/* Loading Progress */}
                          {isOptimizing && (
                            <div className="p-8 bg-[#0a0a0a] border border-emerald-500/20 rounded-[2rem] backdrop-blur-xl max-w-xl mx-auto">
                              <div className="flex items-center gap-4 mb-6">
                                <div className="relative w-10 h-10 flex-shrink-0">
                                  <div className="absolute inset-0 rounded-full border-2 border-emerald-500/20 animate-ping" />
                                  <div className="absolute inset-0 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-black text-white truncate">
                                    {optimizationStage === 1 && (userProfile?.uiLanguage === 'ko' ? 'AI 엔진 설정 로드 중...' : 'Loading AI engine config...')}
                                    {optimizationStage === 2 && (userProfile?.uiLanguage === 'ko' ? 'Mean-Variance 최적화 분석 중...' : 'Running Mean-Variance Optimization...')}
                                    {optimizationStage === 3 && (userProfile?.uiLanguage === 'ko' ? '분석 결과 처리 중...' : 'Processing results...')}
                                    {optimizationStage === 0 && (userProfile?.uiLanguage === 'ko' ? '분석 준비 중...' : 'Preparing...')}
                                  </p>
                                  <p className="text-[11px] text-white/30 mt-1">
                                    {userProfile?.uiLanguage === 'ko' ? '포트폴리오와 매크로 데이터를 AI가 분석합니다. 잠시만 기다려주세요.' : 'AI is analyzing your portfolio and macro data. This may take a moment.'}
                                  </p>
                                </div>
                              </div>
                              {/* Percentage Progress Bar */}
                              <div className="mb-5">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-[10px] font-black uppercase tracking-widest text-white/30">
                                    {userProfile?.uiLanguage === 'ko' ? '분석 진행률' : 'Analysis Progress'}
                                  </span>
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-mono font-black text-emerald-400">
                                      {optimizationStage === 3 ? '100' : Math.round(optimizationProgress)}%
                                    </span>
                                  </div>
                                </div>
                                <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full transition-all duration-500 ease-out relative"
                                    style={{ width: `${optimizationStage === 3 ? 100 : optimizationProgress}%` }}
                                  >
                                    <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full" />
                                  </div>
                                </div>
                              </div>
                              {/* Stage Indicators */}
                              <div className="space-y-2">
                                {[
                                  { stage: 1, label: userProfile?.uiLanguage === 'ko' ? 'AI 엔진 설정' : 'AI Engine Config' },
                                  { stage: 2, label: userProfile?.uiLanguage === 'ko' ? 'Efficient Frontier 계산' : 'Efficient Frontier Calc' },
                                  { stage: 3, label: userProfile?.uiLanguage === 'ko' ? '결과 파싱' : 'Result Parsing' },
                                ].map(({ stage, label }) => (
                                  <div key={stage} className="flex items-center gap-3">
                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-black transition-all duration-500 ${optimizationStage > stage ? 'bg-emerald-500 text-black' :
                                      optimizationStage === stage ? 'bg-emerald-500/30 border border-emerald-500 animate-pulse' :
                                        'bg-white/5 border border-white/10'
                                      }`}>
                                      {optimizationStage > stage ? '✓' : stage}
                                    </div>
                                    <span className={`text-[11px] font-bold transition-all duration-300 ${optimizationStage >= stage ? 'text-white/70' : 'text-white/20'
                                      }`}>{label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* MVO Completed Report */}
                          {optimizationData && !isOptimizing && (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-black uppercase tracking-widest text-white/30">
                                  {userProfile?.uiLanguage === 'ko' ? '퀀트 최적화 리포트' : 'Quant Optimization Report'}
                                </span>
                                <button
                                  onClick={() => handleRunOptimization()}
                                  disabled={!systemFeatures.portfolioOptimizationEnabled}
                                  className={`text-xs font-black tracking-widest uppercase transition-colors ${systemFeatures.portfolioOptimizationEnabled
                                    ? 'text-emerald-400 hover:text-emerald-300 cursor-pointer'
                                    : 'text-white/20 cursor-not-allowed'
                                    }`}
                                >
                                  {userProfile?.uiLanguage === 'ko' ? '다시 분석하기' : 'Re-optimize'}
                                </button>
                              </div>
                              <TierGate
                                requiredTier="TIER3"
                                currentTier={tier}
                                featureName={userProfile?.uiLanguage === 'ko' ? '퀀트 비중 최적화 (MVO)' : 'Quant MVO Optimization'}
                                userProfile={userProfile}
                                onUpgradeSuccess={refreshProfile}
                              >
                                <PortfolioOptimizationReport
                                  data={optimizationData}
                                  language={userProfile?.uiLanguage}
                                  onExecute={async (weights) => {
                                    try {
                                      await updateMultipleTargetWeights(weights);
                                      showToast(userProfile?.uiLanguage === 'ko'
                                        ? '포트폴리오 리밸런싱 최적 비중이 성공적으로 반영되었습니다!'
                                        : 'Optimal rebalancing target weights successfully applied!'
                                      );
                                      setActiveTab('portfolio');
                                    } catch (e) {
                                      console.error("Rebalancing failed", e);
                                      showToast(userProfile?.uiLanguage === 'ko'
                                        ? '비중 반영 중 오류가 발생했습니다.'
                                        : 'An error occurred while applying target weights.',
                                        'error'
                                      );
                                    }
                                  }}
                                  holdings={currentPortfolioHoldings}
                                  signals={signals}
                                  marketMode={portfolioMarketFilter}
                                />
                              </TierGate>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Tab 3: 포트폴리오 X-Ray */}
                      {optSubTab === 'xray' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-300">
                          <PortfolioXRayReport
                            language={userProfile?.uiLanguage}
                            marketMode={portfolioMarketFilter}
                            macroData={macro}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                    {/* 자본시장법 준수 상시 금융투자 데이터 면책 공지 */}
                    <div className="pt-4">
                      <FinancialDisclaimerBanner uiLanguage={userProfile?.uiLanguage} />
                    </div>
                  </div>
                )}

                {activeTab === 'macro' && (
                  <MacroAnalysisView
                    executiveSummary={macro.aiSummary || ""}
                    deepAnalysis={macro.aiIntelligence || ""}
                    isLoading={isDeepAnalysisRunning}
                    isFullHeight={true}
                    language={userProfile?.uiLanguage}
                    analysisProgress={analysisProgress}
                    analysisStage={analysisStage}
                    onStopAnalysis={stopAiAnalysis}
                    reportFormat={macroReportFormat}
                    onSetReportFormat={setMacroReportFormat}
                    isQuickScanLoading={isAnalysisRunning && !isDeepAnalysisRunning}
                    macro={macro}
                    indicators={indicators}
                    marketMode={userProfile?.targetMarket}
                    onFetchIntelligence={fetchIntelligenceOnly}
                    onGenerateQuickSummary={generateQuickSummary}
                    asOfDate={macro.asOfDate || macro.aiAnalysisDate}
                  />
                )}

                {activeTab === 'themes' && (
                  <div className="space-y-8 animate-in fade-in duration-300">
                    <MajorThemeAnalysisWidget
                      selectedAccountId={selectedAccountId}
                      language={userProfile?.uiLanguage}
                      marketMode={userProfile?.targetMarket}
                    />
                  </div>
                )}

                {(activeTab === 'strategy' || activeTab === 'simulator') && (
                  <StrategicSimulatorHub
                    holdings={holdings}
                    signals={signals}
                    macro={macro}
                    indicators={indicators}
                    canAccessLiveTrading={isLiveMode}
                    marketMode={userProfile?.targetMarket}
                    language={userProfile?.uiLanguage}
                    investorType={userProfile?.investorType}
                    selectedAccountId={selectedAccountId}
                    accounts={userAccounts}
                    aiProposedTrades={simulatedAiTrades}
                    onResetAiMode={() => setSimulatedAiTrades(null)}
                    initialSubTab={activeTab === 'strategy' ? 'strategy' : 'simulator'}
                    onSubTabChange={(subTab) => setActiveTab(subTab)}
                    onApply={async (targetWeights) => {
                      const weightsObj: Record<string, number> = {};
                      targetWeights.forEach(tw => {
                        weightsObj[tw.symbol] = tw.weight;
                      });
                      try {
                        await updateMultipleTargetWeights(weightsObj);
                        showToast(userProfile?.uiLanguage === 'ko'
                          ? '리밸런싱 목표 비중이 성공적으로 반영되었습니다!'
                          : 'Rebalancing target weights successfully applied!'
                        );
                        setActiveTab('portfolio');
                      } catch (e) {
                        console.error("Failed to apply rebalancing weights", e);
                        showToast(userProfile?.uiLanguage === 'ko' ? '비중 반영 중 오류가 발생했습니다.' : 'Failed to apply rebalancing weights.', 'error');
                      }
                    }}
                  />
                )}

                {activeTab === 'options' && (
                  <OptionsDataCenter
                    holdings={holdings}
                    watchlist={watchlist}
                    userProfile={userProfile}
                    marketMode={userProfile?.targetMarket}
                    onAnalyzeQuant={(symbol) => {
                      setSelectedStock(symbol);
                      setAnalysisType('quantitative');
                    }}
                  />
                )}

                {activeTab === 'calendar' && (
                  <div className="space-y-8">
                    <div className="flex flex-col gap-1">
                      <h2 className="text-2xl font-black tracking-tight">{userProfile?.uiLanguage === 'ko' ? '포지션 캘린더' : 'Position Calendar'}</h2>
                      <p className="text-sm text-white/40">{userProfile?.uiLanguage === 'ko' ? '주요 일정 및 이벤트가 내 포트폴리오에 미치는 영향 분석' : 'Key events and their impact on your portfolio'}</p>
                    </div>
                    <div className="grid grid-cols-1 gap-8">
                      <PortfolioCalendar
                        holdings={holdings}
                        language={userProfile?.uiLanguage}
                        marketMode={userProfile?.targetMarket}
                        onNavigateToRebalance={() => setActiveTab('portfolio')}
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'history' && (
                  <HistoryView
                    marketMode={portfolioMarketFilter}
                    language={userProfile?.uiLanguage}
                    onNavigateToAnalysis={() => setActiveTab('dashboard')}
                  />
                )}

                {activeTab === 'news' && (
                  <NewsImpactView
                    holdings={holdings}
                    watchlist={watchlist}
                    macro={macro}
                    market={userProfile?.targetMarket || 'US'}
                    language={userProfile?.uiLanguage}
                    userProfile={userProfile}
                    onUpgradeRequest={(requiredTier, featureName) => {
                      handleUpgradeOrAddonRequest(requiredTier, featureName);
                    }}
                    onAddWatchlist={async (symbol, name, market, notes) => {
                      try {
                        await addWatchlist(symbol, name, market, notes);
                        showToast(userProfile?.uiLanguage === 'ko'
                          ? `${symbol}이 관심종목에 추가되었습니다.`
                          : `${symbol} added to watchlist.`
                        );
                      } catch (e) {
                        showToast(userProfile?.uiLanguage === 'ko'
                          ? `관심종목 추가 실패: ${e instanceof Error ? e.message : String(e)}`
                          : `Failed to add to watchlist`, 'error'
                        );
                      }
                    }}
                    onNavigateTo={(tab, symbol) => {
                      setActiveTab(tab as any);
                      if (symbol) {
                        setActiveSimulateSymbol(symbol);
                      }
                    }}
                  />
                )}


                {activeTab === 'guide' && (
                  <UserGuideView language={userProfile?.uiLanguage} isLiveMode={isLiveMode} />
                )}

                

                {activeTab === 'autotrading' && (
                  <AutoTradingReportView
                    language={userProfile?.uiLanguage || 'ko'}
                    macro={macro}
                    holdings={holdings}
                    userProfile={userProfile}
                    marketMode={userProfile?.targetMarket || 'US'}
                    isLiveMode={isLiveMode}
                    onUpdateProfile={(updates: any) => setUserProfile((prev: any) => ({ ...prev, ...updates }))}
                    onUpdateMultipleTargetWeights={updateMultipleTargetWeights}
                    onSaveTradeExecution={saveTradeExecution}
                    onNavigateTab={(tabId: any) => setActiveTab(tabId)}
                  />
                )}

                {activeTab === 'settings' && (
                  <SettingsView
                    user={user}
                    userProfile={userProfile}
                    apiKeys={apiKeys}
                    onSaveApiKeys={saveApiKeys}
                    holdings={holdings}
                    onUpdateTargetWeight={handleUpdateTargetWeight}
                    onUpdateProfile={(updates: any) => setUserProfile((prev: any) => ({ ...prev, ...updates }))}
                    currencyPreference={displayCurrencyPreference}
                    onCurrencyPreferenceChange={setCurrencyPreference}
                    onUpgradeRequest={(requiredTier, featureName, useExistingCard, billingCycle) => {
                      handleUpgradeOrAddonRequest(requiredTier, featureName, billingCycle === 'yearly' ? 'annual' : billingCycle);
                    }}
                    onRefreshProfile={refreshProfile}
                    isAiPersonalizationEnabled={systemFeatures.aiPersonalizationEnabled}
                    featureDisabledMode={systemFeatures.featureDisabledMode}
                  />
                )}

                {activeTab === 'mypage' && (
                  <MyPageView
                    user={user}
                    userProfile={userProfile}
                    onUpdateProfile={(updates: any) => setUserProfile((prev: any) => ({ ...prev, ...updates }))}
                    onUpgradeRequest={(requiredTier, featureName, useExistingCard, billingCycle) => {
                      handleUpgradeOrAddonRequest(requiredTier, featureName, billingCycle === 'yearly' ? 'annual' : billingCycle);
                    }}
                    onRefreshProfile={refreshProfile}
                    isBrokerageSyncEnabled={systemFeatures.brokerageSyncEnabled}
                    isAiPersonalizationEnabled={systemFeatures.aiPersonalizationEnabled}
                    featureDisabledMode={systemFeatures.featureDisabledMode}
                  />
                )}
                </Suspense>

              </div>
            </div>
          </main>
        </div>
      )}

      {isModalOpen && (
        <AddAssetModal
          isOpen={true}
          canAddHoldings={limits?.canAddHoldings !== false}
          onClose={() => {
            setIsModalOpen(false);
            setPrefillSymbol('');
            setPrefillShares(0);
            setPrefillAvgCost(0);
          }}
          onAdd={async (holding, fee) => {
            const currentHoldingsCount = rawHoldings.filter(h => !['USD', 'KRW', 'CASH'].includes(h.symbol.toUpperCase()) && Number(h.shares) > 0).length;
            const isExisting = rawHoldings.some(h => h.symbol.toUpperCase() === holding.symbol.toUpperCase() && Number(h.shares) > 0);
            if (!isExisting && !canAddHolding(currentHoldingsCount)) {
              return;
            }

            try {
              await addHolding({ ...holding, weight: 0, targetWeight: 0 }, fee);
              if (watchlist.some(w => w.symbol.toUpperCase() === holding.symbol.toUpperCase())) {
                await removeWatchlist(holding.symbol, holding.market);
              }
            } catch (error: any) {
              console.error("Failed to add holding:", error);
              window.alert(error.message || '자산 추가 중 오류가 발생했습니다.');
            }
          }}
          language={userProfile?.uiLanguage}
          marketMode={userProfile?.targetMarket}
          defaultSymbol={prefillSymbol}
          defaultShares={prefillShares}
          defaultAvgCost={prefillAvgCost}
          holdingsLimit={limits?.holdingsLimit}
          currentHoldingsCount={rawHoldings.filter(h => !['USD', 'KRW', 'CASH'].includes(h.symbol.toUpperCase()) && Number(h.shares) > 0).length}
        />
      )}

      {isAllocationModalOpen && (
        <AllocationModal
          isOpen={true}
          onClose={() => setIsAllocationModalOpen(false)}
          holdings={holdings}
          language={userProfile?.uiLanguage}
        />
      )}

      {selectedStock && (
        <StockAnalysisSlide
          isOpen={true}
          onClose={() => setSelectedStock(null)}
          symbol={selectedStock}
          macro={indicators}
          signal={signals.find(s => s.symbol === selectedStock)}
          analysisType={analysisType as any}
          language={userProfile?.analysisLanguage || 'ko'}
          userProfile={userProfile}
          onUpgradeRequest={(requiredTier, featureName) => {
            handleUpgradeOrAddonRequest(requiredTier, featureName);
          }}
        />
      )}

      {selectedTechnicalSymbol && (
        <TechnicalAnalysisModal
          isOpen={true}
          symbol={selectedTechnicalSymbol}
          onClose={() => setSelectedTechnicalSymbol(null)}
          language={userProfile?.uiLanguage}
        />
      )}

      {toast && (
        <div className={`fixed bottom-8 right-8 z-[999] flex items-center gap-3 px-6 py-4 rounded-2xl font-black shadow-[0_20px_50px_rgba(0,0,0,0.5)] border animate-in fade-in slide-in-from-bottom-4 duration-300 backdrop-blur-md ${toast.type === 'success'
          ? 'bg-emerald-500/90 hover:bg-emerald-500 text-black border-emerald-400/20'
          : 'bg-rose-500/90 hover:bg-rose-500 text-white border-rose-400/20'
          }`}>
          {toast.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 shrink-0" />
          )}
          <span className="text-sm tracking-tight">{toast.message}</span>
        </div>
      )}

      {upgradeModalConfig && (
        <UpgradeModal
          requiredTier={upgradeModalConfig.requiredTier}
          featureName={upgradeModalConfig.featureName}
          currentTier={tier}
          userProfile={userProfile}
          billingCycle={upgradeModalConfig.billingCycle}
          onClose={() => setUpgradeModalConfig(null)}
          onUpgradeSuccess={async () => {
            await refreshProfile();
          }}
          onNavigateTo={(tab) => {
            setActiveTab(tab as any);
            setUpgradeModalConfig(null);
          }}
        />
      )}

      {addonPaymentModalConfig && (
        <AddonPaymentModal
          isOpen={true}
          addonId={addonPaymentModalConfig.addonId}
          userProfile={userProfile}
          isPaying={isPaymentProcessing}
          onClose={() => setAddonPaymentModalConfig(null)}
          onConfirmPayment={async (addonId, useExisting, paymentType) => {
            setAddonPaymentModalConfig(null);
            await handlePayment(addonId as any, useExisting, 'monthly', paymentType);
          }}
        />
      )}

      {isPaymentProcessing && (
        <div className="fixed inset-0 z-[300] flex flex-col items-center justify-center bg-black/85 backdrop-blur-md">
          <div className="w-12 h-12 border-4 border-t-violet-500 border-violet-500/20 rounded-full animate-spin mb-4" />
          <p className="text-sm font-black text-white/95 tracking-wide animate-pulse">
            {userProfile?.uiLanguage === 'ko' ? '결제를 승인하고 등급을 변경하는 중입니다...' : 'Processing payment approval...'}
          </p>
          <p className="text-xs text-white/40 mt-1.5 font-medium">
            {userProfile?.uiLanguage === 'ko' ? '안전한 처리를 위해 이 창을 닫거나 새로고침하지 마세요.' : 'Please do not close or refresh this window.'}
          </p>
        </div>
      )}

      {/* Floating Action Button (FAB) for Chat Plus */}
      {(!systemFeatures.chatPlusCsEnabled && !systemFeatures.chatPlusTradingEnabled && systemFeatures.featureDisabledMode === 'HIDE') ? null : (
        <button
          onClick={() => setIsChatPlusOpen(!isChatPlusOpen)}
          className={`chatbot-fab fixed bottom-24 md:bottom-6 right-4 md:right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-105 active:scale-95 shadow-2xl border border-white/10
            ${isChatPlusOpen
              ? 'bg-rose-600/95 hover:bg-rose-500 hover:rotate-90'
              : isAddonActive
                ? 'bg-gradient-to-tr from-indigo-600 via-indigo-500 to-purple-500 fab-glow-indigo'
                : 'bg-gradient-to-tr from-neutral-800 via-neutral-700 to-neutral-600 hover:from-neutral-700 hover:to-neutral-600'
            }
          `}
          title="Chat Plus AI 파트너"
          aria-label="Toggle Chat Plus"
        >
          {isChatPlusOpen ? (
            <X className="w-6 h-6 transition-transform duration-300" />
          ) : (
            <div className="relative">
              <Sparkles className="w-6 h-6 animate-pulse" />
              {isAddonActive && (
                <span className="absolute -top-1.5 -right-1.5 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                </span>
              )}
            </div>
          )}
        </button>
      )}

      {user && (
        <ChatPlusModal
          isOpen={isChatPlusOpen}
          onClose={() => setIsChatPlusOpen(false)}
          userProfile={userProfile}
          user={user}
          marketMode={(userProfile?.targetMarket as 'US' | 'KR') || 'US'}
          isLiveMode={isLiveMode}
          chatPlusCsEnabled={systemFeatures.chatPlusCsEnabled}
          chatPlusTradingEnabled={systemFeatures.chatPlusTradingEnabled}
          onOpenAddonModal={(addonId) => {
            setIsChatPlusOpen(false);
            // Tier4 이상이면 바로 결제 진행, 미달이면 업그레이드 모달 표시
            const userTier = userProfile?.membershipTier || 'TIER1';
            if (canAccessTier(userTier, 'TIER4')) {
              setAddonPaymentModalConfig({ addonId: 'CHAT_PREDICTION_ADDON' });
            } else {
              setUpgradeModalConfig({
                requiredTier: 'TIER4',
                featureName: 'Chat Plus (AI 퀀트 & 시뮬레이션 부가상품)'
              });
            }
          }}
        />
      )}

      {isAgentSubModalOpen && (
        <AgentSubscriptionModal
          isOpen={true}
          onClose={() => setIsAgentSubModalOpen(false)}
          userTier={userProfile?.membershipTier || 'TIER1'}
          isLiveMode={isLiveMode}
          onSubscribe={async (addonId) => {
            setIsAgentSubModalOpen(false);
            setAddonPaymentModalConfig({ addonId });
          }}
        />
      )}

      {deviceLimitExceededInfo && (
        <DeviceLimitModal
          isOpen={true}
          limitInfo={deviceLimitExceededInfo}
          onClose={() => setDeviceLimitExceededInfo(null)}
          onSwapped={() => {
            setDeviceLimitExceededInfo(null);
            showToast('등록 디바이스가 교체되었습니다.', 'success');
          }}
          onUpgradeClick={() => {
            setActiveTab('settings');
          }}
        />
      )}

      {paymentSuccessModalData?.isOpen && (
        <PaymentSuccessModal
          isOpen={true}
          onClose={() => setPaymentSuccessModalData(null)}
          tier={paymentSuccessModalData?.tier || 'PRO'}
          expiresAt={paymentSuccessModalData?.expiresAt}
          uiLanguage={userProfile?.uiLanguage || 'ko'}
          onNavigateToFeature={(targetTab) => {
            setPaymentSuccessModalData(null);
            setActiveTab(targetTab as any);
          }}
        />
      )}

      <ReloadPrompt />
    </Suspense>
  </>
  );
}

function NavItem({ icon, label, active, onClick, locked, onLockClick, collapsed = false }: { icon: React.ReactNode; label: string; active: boolean; onClick: () => void; locked?: boolean; onLockClick?: () => void; collapsed?: boolean }) {
  if (locked) {
    return (
      <button
        onClick={onLockClick}
        title={collapsed ? label : undefined}
        className={`w-full flex items-center rounded-2xl transition-all duration-300 group text-white/30 hover:text-amber-400 hover:bg-amber-500/5 cursor-pointer relative ${
          collapsed ? 'justify-center p-3 h-12 w-12 mx-auto' : 'gap-4 px-4 py-3.5 whitespace-nowrap'
        }`}
      >
        <div className="transition-transform duration-300 group-hover:scale-110 shrink-0">
          <Lock className="w-4 h-4" />
        </div>
        {!collapsed && (
          <>
            <span className="font-semibold tracking-tight flex-1 text-left whitespace-nowrap truncate">{label}</span>
            <span className="text-[9px] font-black uppercase tracking-wider text-amber-500/60 bg-amber-500/10 px-2 py-0.5 rounded-full shrink-0">UPGRADE</span>
          </>
        )}
        {collapsed && (
          <div className="absolute left-full ml-3.5 px-3 py-1.5 bg-zinc-900/95 backdrop-blur-md border border-white/15 text-white text-xs font-medium rounded-xl shadow-2xl whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50 flex items-center gap-2">
            <span>{label}</span>
            <span className="text-[9px] font-black uppercase tracking-wider text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded-full">UPGRADE</span>
          </div>
        )}
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={`w-full flex items-center rounded-2xl transition-all duration-300 group relative ${
        collapsed ? 'justify-center p-3 h-12 w-12 mx-auto' : 'gap-4 px-4 py-3.5 whitespace-nowrap'
      } ${active
        ? 'bg-emerald-500 text-black shadow-lg shadow-emerald-500/20'
        : 'text-white/40 hover:text-white hover:bg-white/5'
        }`}
    >
      <div className={`transition-transform duration-300 shrink-0 ${active ? 'scale-110' : 'group-hover:scale-110'}`}>
        {icon}
      </div>
      {!collapsed && (
        <>
          <span className="font-semibold tracking-tight whitespace-nowrap truncate">{label}</span>
          {active && <div className="ml-auto w-1.5 h-1.5 bg-black rounded-full shrink-0" />}
        </>
      )}
      {collapsed && (
        <div className="absolute left-full ml-3.5 px-3 py-1.5 bg-zinc-900/95 backdrop-blur-md border border-white/15 text-white text-xs font-medium rounded-xl shadow-2xl whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50 flex items-center gap-1.5">
          <span>{label}</span>
          {active && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />}
        </div>
      )}
    </button>
  );
}

export default App;
